import { Container, Graphics, Sprite, Text, Texture, type FederatedPointerEvent } from 'pixi.js';
import { draw3DButton, drawBevelPanel, drawHeaderBand, drawStatusBar, GUGI_SHADOW } from '../rendering/PanelUtils';
import { rebuildBuildingSprites, rebuildBuildingLabels, buildingColorToCSS, loadBuildingTextures, type BuildingTextureMap } from '../rendering/BuildingRenderer';
import { getBuildingById } from '../buildings/BuildingData';
import type { GameState, GameStateManager } from './GameStateManager';
import { BaseState } from './BaseState';
import { EndRunState } from './EndRunState';
import { Labyrinth, TILE_SIZE, Tile } from '../labyrinth/Labyrinth';
import { Robot, type EquippedWeaponInfo } from '../labyrinth/Robot';
import type {
  Entity, Enemy, ShielderEnemy } from '../labyrinth/Entity';
import { createEnemy, createPatrolEnemy, createLootChest,
  createChargerEnemy, createBomberEnemy, createShielderEnemy, createBossEnemy,
  updatePatrolEnemy, updateChargerEnemy, updateBomberEnemy, updateShielderEnemy,
} from '../labyrinth/Entity';
import { loadMapFromSVG } from '../labyrinth/MapLoader';
import { scoreMult, type ScoredItem } from '../upgrades/Quality';
import type { UpgradeDef, UpgradeCategory } from '../upgrades/UpgradeData';
import type { WeaponDef } from '../upgrades/WeaponData';
import {
  buildUpgradeCell, buildWeaponCell,
  createUpgradeTooltip, createWeaponTooltip, positionTooltip,
} from '../rendering/InventoryUI';
import { updateStatusEffects, hasStatusEffect, DAMAGE_TYPE_COLORS } from '../labyrinth/StatusEffect';
import { createDeathExplosion } from '../labyrinth/Projectile';
import { SwarmManager } from '../labyrinth/SwarmManager';
import { createSettingsButton, SettingsOverlay } from '../audio/SettingsOverlay';
import { MotorSound } from '../audio/MotorSound';
import { createHudBarTooltip } from '../rendering/TooltipSystem';
import { xpForLevel } from '../upgrades/StatsSystem';
import { getViewport, onViewportChange, type ViewportInfo } from '../rendering/Viewport';
import { enablePinchZoom, enableLongPress, type PinchZoomHandle, type LongPressHandle } from '../input/TouchGestures';
import { MobileActionBar } from '../rendering/MobileActionBar';
import { MobileLootSidebar, type MobileLootSnapshot } from '../rendering/MobileLootSidebar';
import { attachScrollDrag, type ScrollDragHandle } from '../rendering/ScrollablePanel';
import { MARGIN as LC_MARGIN, tabBarH, FONT_DETAIL, TEXT_PRIMARY, ACCENT_DARK } from '../rendering/LayoutConstants';
import { ThreeRenderer } from '../rendering3d/ThreeRenderer';
import { buildAchievementToastOverlay, type AchievementToastOverlay } from '../rendering/AchievementToast';
import { FogMesh3D } from '../rendering3d/FogMesh3D';
import { SpriteBaker } from '../rendering3d/SpriteBaker';
import { createRobotRig, type RobotRig } from '../rendering3d/RobotRig';
import { EntityMeshes } from '../rendering3d/EntityMeshes';
import { ProjectileBillboards } from '../rendering3d/ProjectileBillboards';
import { Vfx3D } from '../rendering3d/Vfx3D';
import { FloatingTexts3D } from '../rendering3d/FloatingTexts3D';
import { Marker3D } from '../rendering3d/Marker3D';
import { Route3D } from '../rendering3d/Route3D';
import { TireTracks3D } from '../rendering3d/TireTracks3D';
import { RadarBlobs3D } from '../rendering3d/RadarBlobs3D';
import { SwarmMeshes3D } from '../rendering3d/SwarmMeshes3D';
import { Lightmap3D } from '../rendering3d/Lightmap3D';
import { WallPulseMap } from '../rendering3d/WallPulseMap';
import { LabTextures } from '../rendering3d/LabyrinthTextures';

// ─── Accent palette (matching base screen — logo red rgb(217,2,33)) ──
const ACCENT       = 0xDD2233;  // bright accent
const ACCENT_MED   = 0xBB1122;  // button fills
const ACCENT_DIM   = 0x881122;  // subtle borders

const MIN_ZOOM = 0.15;
const MAX_ZOOM = 3.0;
const ZOOM_SPEED = 0.15;

// Entity spawn settings
const ENEMY_DENSITY = 0.006;     // per walkable tile (stationary turrets)
const PATROL_DENSITY = 0.003;    // per walkable tile (mobile patrol enemies)
const CHARGER_DENSITY = 0.002;   // per walkable tile (charger enemies)
const BOMBER_DENSITY = 0.0015;   // per walkable tile (bomber enemies)
const SHIELDER_DENSITY = 0.001;  // per walkable tile (shielder enemies)
const LOOT_DENSITY = 0.002;
const SPAWN_MIN_DIST = 15;       // minimum tiles from base
const BOSS_MIN_DIST = 40;        // minimum tiles from base for boss spawns
const BOSS_OPEN_RADIUS = 4;      // radius to count open tiles for boss room detection
const BOSS_OPEN_THRESHOLD = 40;  // minimum open tiles to qualify as boss room
const MAX_BOSSES = 3;            // maximum boss enemies per map

// Enemy visibility fade
const ENEMY_VIS_FADE_TIME = 5.0; // seconds to fade out after losing LOS

// Radar sweep
const RADAR_SPEED = Math.PI * 2 / 3; // one full rotation per 3 seconds
const RADAR_TRAIL_ANGLE = Math.PI * 0.7; // ~126° green trail behind sweep
const RADAR_TRAIL_SEGMENTS = 24;
const RADAR_BLIP_DURATION = 5.0; // seconds a blip stays visible after ping

// Minimap panel dimensions
const MM_MAP_W = 364;
const MM_MAP_H = 308;
const MM_PADDING = 12;
const MM_HEADER_H = 28;
const MM_TOTAL_W = MM_MAP_W + MM_PADDING * 2;
const MM_TOTAL_H = MM_MAP_H + MM_HEADER_H + MM_PADDING * 2;
const MM_MIN_ZOOM = 1;
const MM_MAX_ZOOM = 8;
const MM_ZOOM_SPEED = 0.2;

// Fog of war

// LOD thresholds — below these zoom levels, switch to cheaper rendering

// ¾-view wall front face — visible south-facing side of walls

// Wall drop-shadow — drawn on floor tiles adjacent to walls
// ¾-view: stronger shadows cast south (from north walls), reduced side shadows, no north-cast shadows


export class LabyrinthState implements GameState {
  private container = new Container();
  private mapContainer = new Container();
  private uiContainer = new Container();
  private manager: GameStateManager;

  // 3D prototype renderer (toggle with 'T')
  private threeRenderer: ThreeRenderer | null = null;
  private threeFog: FogMesh3D | null = null;
  private threeBaker: SpriteBaker | null = null;
  private threeEntityMeshes: EntityMeshes | null = null;
  private threeRobotRig: RobotRig | null = null;
  private threeProjectiles: ProjectileBillboards | null = null;
  private threeVfx: Vfx3D | null = null;
  private threeFloatingTexts: FloatingTexts3D | null = null;
  private threeMarker: Marker3D | null = null;
  private threeRoute: Route3D | null = null;
  private threeTireTracks: TireTracks3D | null = null;
  private threeRadarBlobs: RadarBlobs3D | null = null;
  private threeSwarmMeshes: SwarmMeshes3D | null = null;
  private threeLightmap: Lightmap3D | null = null;
  private threeWallPulse: WallPulseMap | null = null;
  private threeResizeHandler: (() => void) | null = null;
  private threeDragStartTile: { x: number; y: number } | null = null;

  private labyrinth!: Labyrinth;
  private robot!: Robot;
  private entities: Entity[] = [];

  // Rendering layers
  private buildingSpriteContainer!: Container;
  private buildingLabelContainer!: Container;
  private buildingTextures: BuildingTextureMap = new Map();
  private statusBarContainer!: Container;
  private statusBarGraphics!: Graphics;
  private statusText!: Text;
  private barLabelPool: Text[] = [];
  private barLabels: Text[] = [];
  private barHeaderLabels: Text[] = [];
  private hudTooltip: Container | null = null;
  private sidebarContainer!: Container;
  private sidebarTooltip: Container | null = null;
  private sidebarCreditsText: Text | null = null;
  private lastLootCount = 0; // track changes to rebuild sidebar
  private combatLogContainer!: Container;
  private lastLogLength = -1;
  private combatLogDirty = false;
  private combatLogThrottle = 0; // seconds since last rebuild
  // Persistent combat log objects (reused to avoid destroy/create churn)
  private combatLogBg: Graphics | null = null;
  private combatLogHeader: Text | null = null;
  private combatLogTexts: Text[] = [];
  private combatLogMobileMode: boolean | null = null;
  // Scrollback: content container is masked; user can wheel/drag to read older entries.
  private combatLogContent: Container | null = null;
  private combatLogScrollY = 0;
  private combatLogMaxScroll = 0;
  private combatLogAutoFollow = true;
  private combatLogScrollDrag: ScrollDragHandle | null = null;
  private minimapContainer!: Container;
  private minimapTimer = 0;
  // Cached bounding box of revealed tiles — recomputed only when revealedCount changes
  private minimapBBoxMinX = 0; private minimapBBoxMinY = 0;
  private minimapBBoxMaxX = 0; private minimapBBoxMaxY = 0;
  // Persistent minimap objects (reused across rebuilds to avoid destroy/create churn)
  private minimapBg: Graphics | null = null;
  private minimapAccent: Graphics | null = null;
  private minimapStatusText: Text | null = null;
  // Mobile drawer — persistent graphics + drag state
  private minimapDrawerBg: Graphics | null = null;
  private minimapDrawerHandle: Container | null = null;
  private minimapDrawerMobileMode: boolean | null = null;
  private minimapDrawerDrag: {
    startY: number;
    startH: number;
    minH: number;
    maxH: number;
    dragged: boolean;
    previewLine: Graphics | null;
    panelX: number;
    panelW: number;
  } | null = null;
  // Minimap map-area dimensions — overridden on mobile to fit the drawer
  private mmMapW = MM_MAP_W;
  private mmMapH = MM_MAP_H;
  private static minimapDrawerHeight: number | null = (() => {
    try { const v = localStorage.getItem('laby.mmDrawer.h'); return v != null ? Number(v) : null; }
    catch { return null; }
  })();
  private static minimapDrawerCollapsed: boolean = (() => {
    try { const v = localStorage.getItem('laby.mmDrawer.collapsed'); return v == null ? true : v === '1'; }
    catch { return true; }
  })();
  private static persistMinimapDrawer() {
    try {
      if (LabyrinthState.minimapDrawerHeight != null)
        localStorage.setItem('laby.mmDrawer.h', String(LabyrinthState.minimapDrawerHeight));
      localStorage.setItem('laby.mmDrawer.collapsed', LabyrinthState.minimapDrawerCollapsed ? '1' : '0');
    } catch { /* ignore */ }
  }
  private _walkableTileCount = -1; // cached, computed once
  // Canvas2D minimap — replaces Graphics rects for massive performance win
  private minimapCanvas: HTMLCanvasElement | null = null;
  private minimapCtx: CanvasRenderingContext2D | null = null;
  private minimapSprite: Sprite | null = null;
  private minimapTexture: Texture | null = null;
  // Radar sweep overlay (drawn every frame on top of the 0.5s tile canvas)
  private radarOverlay: Graphics | null = null;
  private radarAngle = 0;
  // Zoomable minimap content container (holds sprite + radar overlay, masked)
  private minimapContent: Container | null = null;
  private minimapMask: Graphics | null = null;
  private minimapZoom = 1;
  /** Cleared after the first buildMinimap so the default zoom lands the radar
   *  circle at ~50% of the map panel width. User scroll-zooms override it. */
  private minimapZoomInitialised = false;
  // Cached minimap coordinate mapping (set by buildMinimap, read by drawRadarOverlay)
  private mmMinX = 0;
  private mmMinY = 0;
  private mmScale = 1;
  private mmOffsetX = 0;
  private mmOffsetY = 0;
  private motorSound: MotorSound | null = null;
  private stanceBtnBg: Graphics | null = null;
  private stanceBtnIcon: Graphics | null = null;
  private followBtnBg: Graphics | null = null;
  private followBtnIcon: Graphics | null = null;

  // Camera state — camWorldX/Y is the tile-coordinate center of the viewport
  private cameraFollowRobot = true;
  private camWorldX = 0;  // tile coordinates
  private camWorldY = 0;
  private isDragging = false;
  /**
   * True only when the current mousedown landed on the map (not a UI overlay).
   * Guards mouseMoveHandler from panning the camera using stale dragStart
   * values when the user interacts with overlays like SettingsOverlay —
   * otherwise touching a slider or mute button would snap the camera far
   * off-map and produce a black screen.
   */
  private dragStartedOnMap = false;
  private dragStartX = 0;  // screen pixels at drag start
  private dragStartY = 0;
  private dragStartCamWorldX = 0;  // cam world pos at drag start
  private dragStartCamWorldY = 0;
  private mouseMoveHandler: ((e: MouseEvent) => void) | null = null;
  private mouseUpHandler: ((e: MouseEvent) => void) | null = null;


  // Exploration timer
  private elapsedTime = 0;
  // Cached performance.now()/1000 — computed once per frame in update()
  private _frameTime = performance.now() / 1000;
  // Fog tiles already revealed before this deployment
  private revealedAtRunStart = 0;
  // Lighting throttle — skip expensive canvas redraw on alternating frames
  // Dead entity cleanup timer — compact entities array periodically
  private entityCleanupTimer = 0;

  // Swarm system
  private swarmManager!: SwarmManager;
  private swarmCheckTimer = 0;

  // Screen shake
  private shakeIntensity = 0;
  private shakeDecay = 8; // how fast shake fades per second

  // Per-run scratch for achievements (reset at deploy).
  private _deployStartSec = 0;
  private _tilesRevealedAtStart = 0;
  private toastOverlay: AchievementToastOverlay | null = null;

  // Death transition
  private deathTransition = false;
  private deathTransitionTime = 0;
  private deathTransitionCause: 'structure' | 'energy' = 'structure';
  private deathOverlay: Graphics | null = null;
  private deathText: Text | null = null;
  private deathSubText: Text | null = null;
  private deathZoomStart = 0;
  private deathStaticLines: { y: number; speed: number; alpha: number }[] = [];
  private static readonly DEATH_DURATION = 3.0; // seconds before switching state
  private static readonly DEATH_SHAKE_INITIAL = 12; // initial shake intensity
  private static readonly DEATH_ZOOM_SPEED = 0.3; // zoom-in per second


  // Fog of war

  private loading = true;
  private zoom = MAX_ZOOM;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;
  private clickHandler: ((e: MouseEvent) => void) | null = null;
  private contextMenuHandler: ((e: Event) => void) | null = null;

  // Mobile / responsive state
  private vp!: ViewportInfo;
  private vpUnsub: (() => void) | null = null;
  private pinchHandle: PinchZoomHandle | null = null;
  private longPressHandle: LongPressHandle | null = null;
  private lootModal: MobileLootSidebar | null = null;

  // Reusable sets/arrays to avoid per-frame allocation

  constructor(manager: GameStateManager) {
    this.manager = manager;
  }

  enter() {
    const app = this.manager.app;
    this.vp = getViewport(app);
    if (!this.vpUnsub) {
      this.vpUnsub = onViewportChange((v) => {
        this.vp = v;
        this.combatLogDirty = true;
        if (!this.loading) this.buildMinimap();
      });
    }
    app.stage.addChild(this.container);
    this.container.addChild(this.mapContainer);
    this.container.addChild(this.uiContainer);

    // Achievement toast overlay — shared queue with BaseState, so unlocks
    // triggered mid-run surface immediately.
    this.toastOverlay = buildAchievementToastOverlay(this.manager.achievements, app.screen.width);
    this.container.addChild(this.toastOverlay.container);

    // 3D exploration view — the world is rendered by Three.js. Pixi stays
    // rendered (transparent background) for HUD / sidebar / minimap / modals;
    // the legacy 2D mapContainer is permanently hidden.
    this.threeRenderer = new ThreeRenderer();
    this.threeRenderer.setEnabled(true);
    this.mapContainer.visible = false;
    this.threeResizeHandler = () => this.threeRenderer?.resize();
    window.addEventListener('resize', this.threeResizeHandler);

    const loadingText = new Text({
      text: 'Loading map...',
      style: { fontFamily: 'Gugi', fontSize: 28, fill: ACCENT, dropShadow: GUGI_SHADOW },
    });
    loadingText.anchor.set(0.5);
    loadingText.position.set(app.screen.width / 2, app.screen.height / 2);
    this.uiContainer.addChild(loadingText);

    void this.loadMap().then(() => {
      this.uiContainer.removeChild(loadingText);
      loadingText.destroy();
      this.loading = false;
      this.setupScene();
      if (this.threeRenderer) {
        this.threeLightmap = new Lightmap3D(this.labyrinth);
        const lightmapBindings = {
          texture: this.threeLightmap.texture,
          mapSize: this.threeLightmap.mapSize,
        };
        this.threeWallPulse = new WallPulseMap(this.labyrinth);
        const wallPulseBindings = {
          redMask: LabTextures.wallRedMask,
          pulse:   this.threeWallPulse.texture,
          mapSize: this.threeWallPulse.mapSize,
        };
        this.threeRenderer.setLabyrinth(this.labyrinth, lightmapBindings, wallPulseBindings);
        this.threeFog = new FogMesh3D(this.labyrinth, this.manager.exploration!);
        this.threeBaker = new SpriteBaker(this.manager.app.renderer);
        this.threeEntityMeshes = new EntityMeshes(lightmapBindings);
        this.threeRobotRig = createRobotRig(lightmapBindings);
        this.threeProjectiles = new ProjectileBillboards(this.threeBaker);
        this.threeVfx = new Vfx3D(this.threeBaker);
        this.threeFloatingTexts = new FloatingTexts3D();
        this.threeMarker = new Marker3D(this.threeBaker);
        this.threeRoute = new Route3D();
        this.threeTireTracks = new TireTracks3D();
        this.threeRadarBlobs = new RadarBlobs3D();
        this.threeSwarmMeshes = new SwarmMeshes3D(lightmapBindings);
        this.threeRenderer.add(this.threeSwarmMeshes.group);
        this.threeRenderer.add(this.threeEntityMeshes.group);
        this.threeRenderer.add(this.threeRobotRig.root);
        this.threeRenderer.add(this.threeProjectiles.group);
        this.threeRenderer.add(this.threeVfx.group);
        this.threeRenderer.add(this.threeFloatingTexts.group);
        this.threeRenderer.add(this.threeMarker.group);
        this.threeRenderer.add(this.threeRoute.group);
        this.threeRenderer.add(this.threeTireTracks.group);
        this.threeRenderer.add(this.threeRadarBlobs.group);
        this.threeRenderer.add(this.threeFog.group);
      }
      void this.manager.sound.playMusic('/sound/bg-music-exploration.mp3');
      this.motorSound = new MotorSound(this.manager.sound);
      this.motorSound.start(this.robot.x, this.robot.y, this.robot.angle, this.robot.turretAngle);
    });
  }

  private async loadMap() {
    if (!this.manager.labyrinth) {
      const mapData = await loadMapFromSVG('/maps/placeholder-map.svg');
      const labyrinth = new Labyrinth(
        mapData.width,
        mapData.height,
        mapData.tiles,
        mapData.baseTile,
        mapData.doors,
      );
      this.manager.initExploration(labyrinth);
    }
    this.labyrinth = this.manager.labyrinth!;

    // Building textures are still Pixi (rendered in 2D at the base).
    this.buildingTextures = await loadBuildingTextures();
  }

  private setupScene() {
    const app = this.manager.app;
    const exploration = this.manager.exploration!;

    this.revealedAtRunStart = exploration.revealedCount;

    // Spawn robot at selected door (or base center as fallback)
    const selectedDoor = this.labyrinth.getDoor(this.manager.selectedDoor);
    const spawnX = selectedDoor?.x ?? this.labyrinth.baseTile.x;
    const spawnY = selectedDoor?.y ?? this.labyrinth.baseTile.y;
    // Set expedition angle to face outward from the door
    const doorAngles: Record<string, number> = {
      north: -Math.PI / 2, south: Math.PI / 2,
      west: Math.PI, east: 0,
    };
    const initialAngle = selectedDoor ? doorAngles[selectedDoor.direction] : undefined;
    const upgradeStats = this.manager.upgrades.getComputedStats();
    const robotStats = this.manager.stats.computeRobotStats(upgradeStats, {
      damageMult: this.manager.buildings.getDamageMultiplier(),
      damageReduction: this.manager.buildings.getDamageReductionBonus(),
      detectionBonus: this.manager.buildings.getDetectionBonus(),
      visionBonus: this.manager.buildings.getVisionBonus(),
    });

    // Build equipped weapon info array for robot combat
    const equippedWeapons = this.buildEquippedWeapons(robotStats.weaponDamageMult);

    const weaponSlotCount = this.manager.upgrades.weaponSlots;
    this.robot = new Robot(this.labyrinth, exploration, spawnX, spawnY, robotStats, initialAngle, equippedWeapons, weaponSlotCount, this.manager.sound);
    this.robot.qualityBonus = this.manager.buildings.getQualityBonus();
    this.robot.achievements = this.manager.achievements;
    this.camWorldX = spawnX;
    this.camWorldY = spawnY;

    // Spawn entities
    this.spawnEntities();
    this.robot.setEntities(this.entities);

    // Initialize swarm system
    this.swarmManager = new SwarmManager(this.labyrinth, exploration);
    this.robot.setSwarmManager(this.swarmManager);

    // Per-run scratch: start clock + tiles-revealed snapshot for achievements.
    this._deployStartSec = performance.now() / 1000;
    this._tilesRevealedAtStart = exploration.revealedCount;

    // Buildings are still drawn via Pixi (visible only at the base). They live
    // inside the hidden mapContainer so they don't render in the exploration
    // view; loadBuildingTextures() is still needed for the base state.
    this.buildingSpriteContainer = new Container();
    this.buildingLabelContainer = new Container();
    this.mapContainer.addChild(this.buildingSpriteContainer);
    this.mapContainer.addChild(this.buildingLabelContainer);
    this.drawBuildings();

    // ── UI ── Status HUD with bars ─────────────────────────
    const UI_MARGIN = 10;
    this.statusBarContainer = new Container();
    this.statusBarContainer.position.set(UI_MARGIN, UI_MARGIN);
    this.uiContainer.addChild(this.statusBarContainer);
    this.statusBarGraphics = new Graphics();
    this.statusBarContainer.addChild(this.statusBarGraphics);

    // HUD layout constants (must match drawStatusBars)
    const HUD_PAD = 10;
    const HUD_LABEL_H = 14;
    const HUD_LABEL_GAP = 3;
    const HUD_BAR_W = 210, HUD_BAR_H = 24, HUD_BAR_GAP = 8;
    const HUD_BAR_Y_OFFSET = HUD_PAD + HUD_LABEL_H + HUD_LABEL_GAP; // bar top within container

    // HUD bar hover hit areas for tooltips — separate container positioned to overlay the bars
    const barHitContainer = new Container();
    barHitContainer.position.set(UI_MARGIN, UI_MARGIN);
    this.uiContainer.addChild(barHitContainer);
    const barTypes: ('hp' | 'energy' | 'xp')[] = ['hp', 'energy', 'xp'];
    for (let bi = 0; bi < barTypes.length; bi++) {
      const barHit = new Graphics();
      barHit.rect(HUD_PAD + bi * (HUD_BAR_W + HUD_BAR_GAP), HUD_BAR_Y_OFFSET, HUD_BAR_W, HUD_BAR_H);
      barHit.fill({ color: 0x000000, alpha: 0.001 });
      barHit.eventMode = 'static'; barHit.cursor = 'help';
      const barType = barTypes[bi]!;
      barHit.on('pointerenter', () => {
        if (this.hudTooltip) { this.uiContainer.removeChild(this.hudTooltip); this.hudTooltip.destroy({ children: true }); this.hudTooltip = null; }
        const robot = this.robot;
        const sts = this.manager.stats;
        let current = 0, max = 0;
        let extra: { level?: number; xpToNext?: number; unspentPoints?: number; drainPerSec?: number } = {};
        switch (barType) {
          case 'hp': current = robot.structure; max = robot.maxStructure; break;
          case 'energy':
            current = robot.energy; max = robot.maxEnergy;
            break;
          case 'xp':
            current = sts.totalXp; max = sts.totalXp + sts.xpToNext;
            extra = { level: sts.level, xpToNext: sts.xpToNext, unspentPoints: sts.unspentPoints };
            break;
        }
        const { tip, width } = createHudBarTooltip(barType, current, max, extra);
        const tipX = UI_MARGIN + HUD_PAD + bi * (HUD_BAR_W + HUD_BAR_GAP);
        const tipY = UI_MARGIN + HUD_BAR_Y_OFFSET + HUD_BAR_H + 4;
        tip.position.set(tipX, tipY);
        if (tip.x + width > app.screen.width - 10) tip.x = app.screen.width - width - 10;
        this.uiContainer.addChild(tip);
        this.hudTooltip = tip;
      });
      barHit.on('pointerleave', () => {
        if (this.hudTooltip) { this.uiContainer.removeChild(this.hudTooltip); this.hudTooltip.destroy({ children: true }); this.hudTooltip = null; }
      });
      barHitContainer.addChild(barHit);
    }

    // Status text for state label + enemy info (right side)
    this.statusText = new Text({
      text: '',
      style: {
        fontFamily: 'Offside', fontSize: 17, fill: 0xaa6677,
        dropShadow: { color: 0x000000, blur: 2, distance: 1, alpha: 0.8 },
      },
    });
    this.statusText.position.set(0, 0);
    this.statusBarContainer.addChild(this.statusText);

    const isMobile = this.vp.mode === 'mobile';

    // Sidebar container — always created (mobile uses it to stash nothing, but
    // build/teardown code elsewhere references sidebarContainer unconditionally).
    this.sidebarContainer = new Container();
    this.uiContainer.addChild(this.sidebarContainer);
    // Combat log — bottom left
    this.combatLogContainer = new Container();
    this.uiContainer.addChild(this.combatLogContainer);
    // Minimap container — always created; only populated on desktop.
    this.minimapContainer = new Container();
    this.uiContainer.addChild(this.minimapContainer);

    if (!isMobile) {
      // Recall button — sized to match sidebar panel
      const sidebarCols = 5;
      const sidebarCellSize = 40;
      const sidebarCellGap = 3;
      const sidebarPanelW = sidebarCols * (sidebarCellSize + sidebarCellGap) + sidebarCellGap + 12;
      const sidebarMargin = 10;
      const sidebarPadding = 6;
      const btnW = sidebarPanelW + sidebarPadding * 2;
      const btnX = app.screen.width - sidebarPanelW - sidebarMargin - sidebarPadding;

      const recallBtn = new Container();
      const btnBg = new Graphics();
      draw3DButton(btnBg, btnW, 44, ACCENT_MED);
      recallBtn.addChild(btnBg);

      const btnText = new Text({
        text: 'RECALL',
        style: { fontFamily: 'Gugi', fontSize: 20, fill: 0xffffff, dropShadow: GUGI_SHADOW },
      });
      btnText.position.set(btnW / 2, 22);
      btnText.anchor.set(0.5);
      recallBtn.addChild(btnText);

      recallBtn.position.set(btnX, 10);
      recallBtn.eventMode = 'static';
      recallBtn.cursor = 'pointer';
      recallBtn.on('pointerdown', () => {
        this.transferXpAndRecall();
      });
      this.uiContainer.addChild(recallBtn);

      // Settings button — just left of the recall button
      createSettingsButton(this.uiContainer, app, this.manager.sound, btnX - 46, 10, this.manager);

      // Stance toggle button — just left of the settings button
      this.buildStanceButton(btnX - 46 - 42, 10);

      // Follow-robot camera button — just left of the stance button
      this.buildFollowButton(btnX - 46 - 42 - 42, 10);

      this.buildSidebar();
      this.buildMinimap();
    } else {
      // Mobile: scale the desktop-sized HUD bars to fit the viewport
      const DESKTOP_BACKDROP_W = 3 * 210 + 2 * 8 + 10 * 2; // 646
      const available = this.vp.w - 20;
      const scale = Math.min(1, available / DESKTOP_BACKDROP_W);
      this.statusBarContainer.scale.set(scale);
      barHitContainer.scale.set(scale);

      this.buildMobileActionBar();
      this.buildMinimap();

      // Global pointer listeners for drawer drag (resize + release)
      this.uiContainer.eventMode = 'static';
      this.uiContainer.on('pointermove', (e: FederatedPointerEvent) => this.onMinimapDrawerMove(e));
      this.uiContainer.on('pointerup', (e: FederatedPointerEvent) => this.onMinimapDrawerUp(e));
      this.uiContainer.on('pointerupoutside', (e: FederatedPointerEvent) => this.onMinimapDrawerUp(e));
    }

    // ── Input ──────────────────────────────────────────────
    // Zoom
    this.wheelHandler = (e: WheelEvent) => {
      e.preventDefault();

      // Block zoom when cursor is over a UI overlay (settings, changelog, etc.)
      const rect = app.canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const hit = app.renderer.events.rootBoundary.hitTest(mx, my);
      if (hit && hit !== app.stage) return;

      const dir = e.deltaY < 0 ? 1 : -1;

      // Check if cursor is over the minimap panel
      const mmX = this.minimapContainer.x;
      const mmY = this.minimapContainer.y;
      if (mx >= mmX && mx <= mmX + MM_TOTAL_W && my >= mmY && my <= mmY + MM_TOTAL_H) {
        this.minimapZoom *= 1 + dir * MM_ZOOM_SPEED;
        this.minimapZoom = Math.max(MM_MIN_ZOOM, Math.min(MM_MAX_ZOOM, this.minimapZoom));
      } else {
        this.zoom *= 1 + dir * ZOOM_SPEED;
        this.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.zoom));
      }
    };
    app.canvas.addEventListener('wheel', this.wheelHandler, { passive: false });

    // Click to place marker / drag to pan camera
    this.clickHandler = (e: MouseEvent) => {
      const rect = app.canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      // Don't interact if the click lands on a UI element
      const hit = app.renderer.events.rootBoundary.hitTest(screenX, screenY);
      if (hit && hit !== app.stage) {
        this.dragStartedOnMap = false;
        return;
      }

      // Right-click removes marker
      if (e.button === 2) {
        this.robot.clearMarker();
        return;
      }

      // Left-click starts potential drag
      if (e.button === 0) {
        this.isDragging = false;
        this.dragStartedOnMap = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        this.dragStartCamWorldX = this.camWorldX;
        this.dragStartCamWorldY = this.camWorldY;
        this.threeDragStartTile = this.threeRenderer?.screenToTile(e.clientX, e.clientY) ?? null;
      }
    };
    app.canvas.addEventListener('mousedown', this.clickHandler);

    this.mouseMoveHandler = (e: MouseEvent) => {
      if ((e.buttons & 1) && this.dragStartedOnMap) { // left button held after a valid map-origin mousedown
        const dx = e.clientX - this.dragStartX;
        const dy = e.clientY - this.dragStartY;
        if (!this.isDragging && (dx * dx + dy * dy) > 16) { // 4px deadzone
          this.isDragging = true;
        }
        if (this.isDragging) {
          // Keep the world point originally under cursor stuck to cursor.
          const start = this.threeDragStartTile;
          if (start && this.threeRenderer) {
            const cur = this.threeRenderer.screenToTileAtCamera(
              this.dragStartCamWorldX, this.dragStartCamWorldY, this.zoom, e.clientX, e.clientY,
            );
            if (cur) {
              this.camWorldX = this.dragStartCamWorldX + (start.x - cur.x);
              this.camWorldY = this.dragStartCamWorldY + (start.y - cur.y);
            }
          }
          this.cameraFollowRobot = false;
          this.updateFollowButton();
        }
      }
    };
    app.canvas.addEventListener('mousemove', this.mouseMoveHandler);

    this.mouseUpHandler = (e: MouseEvent) => {
      const wasOnMap = this.dragStartedOnMap;
      this.dragStartedOnMap = false;
      if (e.button === 0 && !this.isDragging && wasOnMap) {
        // Don't place marker if the click lands on a UI element
        const rect = app.canvas.getBoundingClientRect();
        const screenX = e.clientX - rect.left;
        const screenY = e.clientY - rect.top;
        const hit = app.renderer.events.rootBoundary.hitTest(screenX, screenY);
        if (hit && hit !== app.stage) { this.isDragging = false; return; }
        // Was a click, not a drag — place marker via 3D raycast
        const tile = this.threeRenderer?.screenToTile(e.clientX, e.clientY);
        if (tile) this.robot.setMarker(tile.x, tile.y);
      }
      this.isDragging = false;
      this.threeDragStartTile = null;
    };
    app.canvas.addEventListener('mouseup', this.mouseUpHandler);

    this.contextMenuHandler = (e: Event) => e.preventDefault();
    app.canvas.addEventListener('contextmenu', this.contextMenuHandler);

    // Touch gestures — pinch zoom on the canvas (parallel to wheel) and
    // long-press on the map for marker clear (parallel to right-click).
    this.pinchHandle = enablePinchZoom(app.canvas, (factor) => {
      this.zoom *= factor;
      this.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.zoom));
    });
    this.longPressHandle = enableLongPress(this.mapContainer, () => {
      this.robot.clearMarker();
    });
  }

  private buildMobileActionBar() {
    const app = this.manager.app;
    const vp = this.vp;
    const bar = new MobileActionBar(
      [
        { icon: '\uD83D\uDDFA', label: 'MAP',    onPress: () => this.toggleMinimapDrawer() },
        { icon: '\uD83C\uDFAF', label: 'AGGRO', onPress: () => {
          this.robot.passive = !this.robot.passive;
        }, getActive: () => !this.robot.passive },
        { icon: '\uD83D\uDC63', label: 'FOLLOW', onPress: () => {
          this.cameraFollowRobot = !this.cameraFollowRobot;
        }, getActive: () => this.cameraFollowRobot },
        { icon: '\uD83C\uDF92', label: 'LOOT',   onPress: () => this.openMobileLootModal() },
        { icon: '\u2699',       label: 'SET',    onPress: () => {
          this.manager.sound.resume();
          new SettingsOverlay(app, this.manager.sound, this.manager);
        } },
        { icon: '\u21A9',       label: 'RECALL', onPress: () => this.transferXpAndRecall(), tint: ACCENT },
      ],
      { width: vp.w, safeBottom: vp.safeBottom },
    );
    bar.position.set(0, app.screen.height - 64 - vp.safeBottom);
    this.uiContainer.addChild(bar);
  }

  private openMobileLootModal() {
    if (this.lootModal) return;
    const vp = this.vp;
    const ups = this.manager.upgrades;
    const cats: UpgradeCategory[] = ['engine', 'sensors', 'capacitor', 'shield', 'chassis'];
    const snapshot: MobileLootSnapshot = {
      weapons: ups.getEquippedWeapons(),
      equipment: cats
        .map(c => { const eq = ups.getEquipped(c); return eq ? { cat: c, def: eq.def, item: eq.item } : null; })
        .filter((e): e is { cat: UpgradeCategory; def: UpgradeDef; item: ScoredItem } => e !== null),
      loot: this.robot.lootBag.map(l => ({ def: l.def, item: l.item })),
      coinsThisRun: this.coinsThisRun,
    };
    const modal = new MobileLootSidebar({
      vpW: vp.w, vpH: vp.h, safeTop: vp.safeTop, safeBottom: vp.safeBottom,
      snapshot,
      onClose: () => this.closeMobileLootModal(),
    });
    this.uiContainer.addChild(modal);
    this.lootModal = modal;
  }

  private closeMobileLootModal() {
    if (!this.lootModal) return;
    this.uiContainer.removeChild(this.lootModal);
    this.lootModal.destroy({ children: true });
    this.lootModal = null;
  }

  // -------------------------------------------------------
  // Build weapon info for robot from UpgradeSystem
  // -------------------------------------------------------
  private buildEquippedWeapons(weaponDamageMult: number): EquippedWeaponInfo[] {
    const result: EquippedWeaponInfo[] = [];
    const weapons = this.manager.upgrades.getEquippedWeapons();

    for (const w of weapons) {
      if (!w) continue;
      const { def, item } = w;
      const qm = scoreMult(item.score);
      result.push({
        id: def.id,
        category: def.category,
        tier: def.tier,
        damage: def.stats.damage * qm * weaponDamageMult,
        attackSpeed: def.stats.attackSpeed,
        range: def.stats.range,
        aoeRadius: (def.stats.aoeRadius ?? 0) * qm,
        energyCost: def.stats.energyCost ?? 0,
        burstCount: def.stats.burstCount ?? 1,
        knockback: (def.stats.knockback ?? 0) * qm,
        // Unique weapon behavior fields
        pierceCount: def.stats.pierceCount ?? 0,
        homingStrength: def.stats.homingStrength ?? 0,
        projectileCount: def.stats.projectileCount ?? 1,
        spreadAngle: def.stats.spreadAngle ?? 0,
        speedMult: def.stats.speedMult ?? 1,
        fragmentCount: def.stats.fragmentCount ?? 0,
        pullStrength: def.stats.pullStrength ?? 0,
        lingerDuration: def.stats.lingerDuration ?? 0,
        lingerDps: def.stats.lingerDps ?? 0,
        damageFalloff: def.stats.damageFalloff ?? false,
        doubleStrike: def.stats.doubleStrike ?? false,
        bonusDamageMult: def.stats.bonusDamageMult ?? 1,
        splashRadius: def.stats.splashRadius ?? 0,
        statusEffect: def.stats.statusEffect ?? null,
      });
    }
    return result;
  }

  // -------------------------------------------------------
  // Entity spawning
  // -------------------------------------------------------
  private spawnEntities() {
    this.entities = [];
    const lab = this.labyrinth;
    const base = lab.baseTile;

    // Collect walkable floor tiles — exclude base/door tiles and tiles too close to base
    const candidates: [number, number][] = [];
    for (let y = 0; y < lab.height; y++) {
      for (let x = 0; x < lab.width; x++) {
        if (!lab.isWalkable(x, y)) continue;
        const tile = lab.tiles[y]![x];
        if (tile === Tile.Base || tile === Tile.Door) continue;
        const dist = Math.abs(x - base.x) + Math.abs(y - base.y);
        if (dist < SPAWN_MIN_DIST) continue;
        candidates.push([x, y]);
      }
    }

    // --- Boss spawn: detect large open areas ---
    const bossLocations = this.findLargeOpenAreas(lab, base);
    const bossKinds: Array<'turret' | 'charger' | 'shielder'> = ['turret', 'charger', 'shielder'];
    for (let i = 0; i < bossLocations.length; i++) {
      const loc = bossLocations[i]!;
      const tier = Math.min(5, 1 + Math.floor(loc.dist / 30));
      const kind = bossKinds[i % bossKinds.length]!;
      this.entities.push(createBossEnemy(kind, loc.x + 0.5, loc.y + 0.5, tier));
    }

    // Shuffle candidates
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j]!, candidates[i]!];
    }

    const enemyCount = Math.floor(candidates.length * ENEMY_DENSITY);
    const patrolCount = Math.floor(candidates.length * PATROL_DENSITY);
    const chargerCount = Math.floor(candidates.length * CHARGER_DENSITY);
    const bomberCount = Math.floor(candidates.length * BOMBER_DENSITY);
    const shielderCount = Math.floor(candidates.length * SHIELDER_DENSITY);
    const lootCount = Math.floor(candidates.length * LOOT_DENSITY);
    let idx = 0;

    // Spawn stationary turret enemies
    for (let i = 0; i < enemyCount && idx < candidates.length; i++, idx++) {
      const [x, y] = candidates[idx]!;
      const distFromBase = Math.abs(x - base.x) + Math.abs(y - base.y);
      const tier = Math.min(5, 1 + Math.floor(distFromBase / 30));
      this.entities.push(createEnemy(x + 0.5, y + 0.5, tier));
    }

    // Spawn mobile patrol enemies
    for (let i = 0; i < patrolCount && idx < candidates.length; i++, idx++) {
      const [x, y] = candidates[idx]!;
      const distFromBase = Math.abs(x - base.x) + Math.abs(y - base.y);
      const tier = Math.min(5, 1 + Math.floor(distFromBase / 30));
      this.entities.push(createPatrolEnemy(x + 0.5, y + 0.5, tier));
    }

    // Spawn charger enemies
    for (let i = 0; i < chargerCount && idx < candidates.length; i++, idx++) {
      const [x, y] = candidates[idx]!;
      const distFromBase = Math.abs(x - base.x) + Math.abs(y - base.y);
      const tier = Math.min(5, 1 + Math.floor(distFromBase / 30));
      this.entities.push(createChargerEnemy(x + 0.5, y + 0.5, tier));
    }

    // Spawn bomber enemies
    for (let i = 0; i < bomberCount && idx < candidates.length; i++, idx++) {
      const [x, y] = candidates[idx]!;
      const distFromBase = Math.abs(x - base.x) + Math.abs(y - base.y);
      const tier = Math.min(5, 1 + Math.floor(distFromBase / 30));
      this.entities.push(createBomberEnemy(x + 0.5, y + 0.5, tier));
    }

    // Spawn shielder enemies
    for (let i = 0; i < shielderCount && idx < candidates.length; i++, idx++) {
      const [x, y] = candidates[idx]!;
      const distFromBase = Math.abs(x - base.x) + Math.abs(y - base.y);
      const tier = Math.min(5, 1 + Math.floor(distFromBase / 30));
      this.entities.push(createShielderEnemy(x + 0.5, y + 0.5, tier));
    }

    // Spawn loot
    for (let i = 0; i < lootCount && idx < candidates.length; i++, idx++) {
      const [x, y] = candidates[idx]!;
      const distFromBase = Math.abs(x - base.x) + Math.abs(y - base.y);
      const tier = Math.min(5, 1 + Math.floor(distFromBase / 30));
      this.entities.push(createLootChest(x + 0.5, y + 0.5, tier));
    }
  }

  /** Detect large open areas in the labyrinth for boss placement using radius-count heuristic */
  private findLargeOpenAreas(lab: Labyrinth, base: { x: number; y: number }): { x: number; y: number; dist: number }[] {
    const r = BOSS_OPEN_RADIUS;
    const scores: { x: number; y: number; score: number; dist: number }[] = [];

    // Score each walkable tile by how many walkable tiles exist within radius
    for (let y = r; y < lab.height - r; y++) {
      for (let x = r; x < lab.width - r; x++) {
        if (!lab.isWalkable(x, y)) continue;
        const dist = Math.abs(x - base.x) + Math.abs(y - base.y);
        if (dist < BOSS_MIN_DIST) continue;

        let count = 0;
        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            if (dx * dx + dy * dy > r * r) continue; // circular radius
            if (lab.isWalkable(x + dx, y + dy)) count++;
          }
        }

        if (count >= BOSS_OPEN_THRESHOLD) {
          scores.push({ x, y, score: count, dist });
        }
      }
    }

    // Sort by score descending (largest open areas first)
    scores.sort((a, b) => b.score - a.score);

    // Pick up to MAX_BOSSES locations, ensuring they're at least 30 tiles apart
    const result: { x: number; y: number; dist: number }[] = [];
    for (const s of scores) {
      if (result.length >= MAX_BOSSES) break;
      const tooClose = result.some(r => Math.hypot(r.x - s.x, r.y - s.y) < 30);
      if (!tooClose) {
        result.push({ x: s.x, y: s.y, dist: s.dist });
      }
    }

    return result;
  }

  exit() {
    this.toastOverlay = null;
    this.motorSound?.stop();
    this.motorSound = null;
    if (this.wheelHandler) {
      this.manager.app.canvas.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
    if (this.clickHandler) {
      this.manager.app.canvas.removeEventListener('mousedown', this.clickHandler);
      this.clickHandler = null;
    }
    if (this.mouseMoveHandler) {
      this.manager.app.canvas.removeEventListener('mousemove', this.mouseMoveHandler);
      this.mouseMoveHandler = null;
    }
    if (this.mouseUpHandler) {
      this.manager.app.canvas.removeEventListener('mouseup', this.mouseUpHandler);
      this.mouseUpHandler = null;
    }
    if (this.contextMenuHandler) {
      this.manager.app.canvas.removeEventListener('contextmenu', this.contextMenuHandler);
      this.contextMenuHandler = null;
    }
    if (this.pinchHandle) { this.pinchHandle.dispose(); this.pinchHandle = null; }
    if (this.longPressHandle) { this.longPressHandle.dispose(); this.longPressHandle = null; }
    if (this.vpUnsub) { this.vpUnsub(); this.vpUnsub = null; }
    this.lootModal = null;
    // Cleanup death transition objects (they're children of container, destroyed below)
    this.deathOverlay = null;
    this.deathText = null;
    this.deathSubText = null;

    if (this.threeResizeHandler) {
      window.removeEventListener('resize', this.threeResizeHandler);
      this.threeResizeHandler = null;
    }
    this.threeDragStartTile = null;
    if (this.threeWallPulse) {
      this.threeWallPulse.dispose();
      this.threeWallPulse = null;
    }
    if (this.threeLightmap) {
      this.threeLightmap.dispose();
      this.threeLightmap = null;
    }
    if (this.threeRadarBlobs) {
      this.threeRadarBlobs.dispose();
      this.threeRadarBlobs = null;
    }
    if (this.threeTireTracks) {
      this.threeTireTracks.dispose();
      this.threeTireTracks = null;
    }
    if (this.threeRoute) {
      this.threeRoute.dispose();
      this.threeRoute = null;
    }
    if (this.threeMarker) {
      this.threeMarker.dispose();
      this.threeMarker = null;
    }
    if (this.threeFloatingTexts) {
      this.threeFloatingTexts.dispose();
      this.threeFloatingTexts = null;
    }
    if (this.threeVfx) {
      this.threeVfx.dispose();
      this.threeVfx = null;
    }
    if (this.threeProjectiles) {
      this.threeProjectiles.dispose();
      this.threeProjectiles = null;
    }
    if (this.threeSwarmMeshes) {
      this.threeSwarmMeshes.dispose();
      this.threeSwarmMeshes = null;
    }
    if (this.threeEntityMeshes) {
      this.threeEntityMeshes.dispose();
      this.threeEntityMeshes = null;
    }
    if (this.threeRobotRig) {
      this.threeRenderer?.remove(this.threeRobotRig.root);
      this.threeRobotRig.dispose();
      this.threeRobotRig = null;
    }
    if (this.threeBaker) {
      this.threeBaker.dispose();
      this.threeBaker = null;
    }
    if (this.threeFog) {
      this.threeRenderer?.remove(this.threeFog.group);
      this.threeFog.dispose();
      this.threeFog = null;
    }
    if (this.threeRenderer) {
      this.threeRenderer.dispose();
      this.threeRenderer = null;
    }

    this.manager.app.stage.removeChild(this.container);
    this.container.destroy({ children: true });
    // Cleanup minimap canvas texture
    if (this.minimapTexture) {
      this.minimapTexture.destroy(true);
      this.minimapTexture = null;
    }
    this.minimapCanvas = null;
    this.minimapCtx = null;
    this.minimapSprite = null;
  }

  update(dt: number) {
    if (this.loading) return;

    this._frameTime = performance.now() / 1000;
    this.elapsedTime += dt;
    this.toastOverlay?.update(dt);

    this.robot.update(dt);
    this.motorSound?.update(dt, this.robot.x, this.robot.y, this.robot.angle, this.robot.turretAngle, this.robot.state);

    // Update patrol enemy movement + status effects
    // Skip patrol movement for enemies beyond active range (detection radius ~7 + 7 tile buffer)
    const activeRange = 14;
    const rx = this.robot.x;
    const ry = this.robot.y;

    // Reuse the alive-enemies list Robot just populated at the top of its
    // own update — this used to be a second identical pass over `entities`.
    const aliveEnemies = this.robot.aliveEnemies;

    for (const enemy of aliveEnemies) {
        // Always tick status effects if the enemy has any active ones
        if (enemy.statusEffects.length > 0) {
          const tick = updateStatusEffects(enemy, dt);
          if (tick.burnDamage > 0) {
            enemy.hp -= tick.burnDamage;
            if (enemy.hp <= 0) {
              enemy.hp = 0;
              enemy.alive = false;
              // Death explosion for burn kills
              this.robot.vfx.push(createDeathExplosion(enemy.x, enemy.y, DAMAGE_TYPE_COLORS['energy']));
              void this.manager.sound?.playSound('/sound/explosion.mp3', 1.5);
            }
          }
        }
        // Only update movement for enemies within active range
        const distSq = (enemy.x - rx) * (enemy.x - rx) + (enemy.y - ry) * (enemy.y - ry);
        if (distSq <= activeRange * activeRange) {
          switch (enemy.enemyKind) {
            case 'turret': {
              // Rotate turret to face the player
              if (!hasStatusEffect(enemy, 'stun')) {
                const targetAngle = Math.atan2(ry - enemy.y, rx - enemy.x);
                let diff = targetAngle - enemy.angle;
                // Normalize to [-PI, PI]
                diff = ((diff + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
                const rotSpeed = 2.0; // radians per second
                if (Math.abs(diff) < rotSpeed * dt) {
                  enemy.angle = targetAngle;
                } else {
                  enemy.angle += Math.sign(diff) * rotSpeed * dt;
                }
              }
              break;
            }
            case 'charger':
              updateChargerEnemy(enemy, dt, this.labyrinth, rx, ry);
              break;
            case 'bomber':
              updateBomberEnemy(enemy, dt, this.labyrinth);
              break;
            case 'shielder':
              updateShielderEnemy(enemy, dt, this.labyrinth, aliveEnemies);
              break;
            default:
              updatePatrolEnemy(enemy, dt, this.labyrinth);
              break;
          }
        }
    }

    // Boss Warden: regenerate HP for nearby allies + periodically spawn minions
    // Pre-compute lists to avoid O(n²) inner loops over all entities.
    // Typed as ShielderEnemy[] so auraRadius / shieldReduction accesses
    // typecheck without further narrowing inside the loop.
    const shielderBosses: ShielderEnemy[] = [];
    for (const entity of this.entities) {
      if (entity.type === 'enemy' && entity.alive) {
        const enemy = entity as Enemy;
        if (enemy.isBoss && enemy.enemyKind === 'shielder') {
          shielderBosses.push(enemy);
        }
      }
    }

    if (shielderBosses.length > 0) {
      // Iterate alive enemies once, applying aura regen from any nearby boss
      for (const entity of this.entities) {
        if (entity.type !== 'enemy' || !entity.alive) continue;
        const ally = entity as Enemy;
        for (const boss of shielderBosses) {
          if (ally === boss) continue;
          const d = Math.hypot(ally.x - boss.x, ally.y - boss.y);
          if (d <= boss.auraRadius) {
            ally.hp = Math.min(ally.maxHp, ally.hp + 2 * dt);
            break; // only one boss aura per tick per ally
          }
        }
      }

      // Spawn minions for each boss
      for (const boss of shielderBosses) {
        boss.bossPhase += dt;
        if (boss.bossPhase >= 8) {
          boss.bossPhase = 0;
          // Count alive enemies nearby to cap at 4
          let minionCount = 0;
          const auraDist2 = (boss.auraRadius * 2) ** 2;
          for (const entity of this.entities) {
            if (entity.type !== 'enemy' || !entity.alive || entity === boss) continue;
            const a = entity as Enemy;
            const dx = a.x - boss.x;
            const dy = a.y - boss.y;
            if (dx * dx + dy * dy <= auraDist2) minionCount++;
          }
          if (minionCount < 4) {
            const angle = Math.random() * Math.PI * 2;
            const dist = 2 + Math.random() * 2;
            const mx = boss.x + Math.cos(angle) * dist;
            const my = boss.y + Math.sin(angle) * dist;
            if (this.labyrinth.isWalkable(Math.floor(mx), Math.floor(my))) {
              const distFromBase = Math.abs(mx - this.labyrinth.baseTile.x) + Math.abs(my - this.labyrinth.baseTile.y);
              const tier = Math.min(5, 1 + Math.floor(distFromBase / 30));
              this.entities.push(createPatrolEnemy(mx, my, tier));
              this.robot.setEntities(this.entities);
            }
          }
        }
      }
    }

    // Periodically remove dead entities to prevent unbounded array growth
    // (boss shielders spawn minions every 8s — dead ones accumulate forever without this)
    this.entityCleanupTimer += dt;
    if (this.entityCleanupTimer >= 2) {
      this.entityCleanupTimer = 0;
      const cleanupDistSq = 30 * 30;
      const MAX_ENTITIES = 200;
      let needsSync = false;

      // Pass 1: remove dead entities far from the robot, and dead non-enemy entities everywhere
      for (let i = this.entities.length - 1; i >= 0; i--) {
        const e = this.entities[i]!;
        if (e.alive) continue;
        // Always remove dead non-enemy entities (loot pickups, etc. already collected)
        if (e.type !== 'enemy') {
          this.entities.splice(i, 1);
          needsSync = true;
          continue;
        }
        const dx = e.x - rx;
        const dy = e.y - ry;
        if (dx * dx + dy * dy > cleanupDistSq) {
          this.entities.splice(i, 1);
          needsSync = true;
        }
      }

      // Pass 2: hard cap — if still over limit, force-remove oldest dead entities regardless of distance
      if (this.entities.length > MAX_ENTITIES) {
        // Collect indices of all dead entities (oldest first = lowest index)
        const deadIndices: number[] = [];
        for (let i = 0; i < this.entities.length; i++) {
          if (!this.entities[i]!.alive) deadIndices.push(i);
        }
        const toRemove = this.entities.length - MAX_ENTITIES;
        // Remove from end to preserve indices
        const removeSet = deadIndices.slice(0, toRemove);
        for (let j = removeSet.length - 1; j >= 0; j--) {
          this.entities.splice(removeSet[j]!, 1);
          needsSync = true;
        }
      }

      if (needsSync) {
        this.robot.setEntities(this.entities);
      }
    }

    // Swarm system — check trigger every 1s, then update movement/spawning/combat
    this.swarmCheckTimer += dt;
    if (this.swarmCheckTimer >= 1.0) {
      this.swarmCheckTimer = 0;
      const pct = this.manager.getExplorationPct();
      this.swarmManager.checkTrigger(pct);
    }
    this.swarmManager.update(
      dt, rx, ry, this.robot.damageReduction,
      (dmg: number) => {
        this.robot.structure -= dmg;
        this.robot.damageTakenThisRun += dmg;
        if (this.robot.structure < this.robot.maxStructure * 0.1) this.robot.lowHpEverThisRun = true;
        this.robot.floatingTexts.push({
          text: `${Math.round(dmg)}`, color: 0xff4444,
          x: this.robot.x, y: this.robot.y - 0.3,
          vx: (Math.random() - 0.5) * 0.8,
          life: 1.2, maxLife: 1.2, big: false,
        });
      },
      this.robot.vfx,
    );
    // Track per-run max Chebyshev distance from spawn (for Far Frontier).
    {
      const d = Math.max(
        Math.abs(this.robot.x - this.robot.baseX),
        Math.abs(this.robot.y - this.robot.baseY),
      );
      if (d > this.robot.maxDistFromSpawnThisRun) this.robot.maxDistFromSpawnThisRun = d;
    }

    // Transfer swarm XP/kills to robot
    if (this.swarmManager.frameXpGained > 0) {
      this.robot.xpGained += this.swarmManager.frameXpGained;
      this.robot.killCount += this.swarmManager.frameKills;
      // Achievement: swarm kill counters (batched once per frame; recheck at end).
      const ach = this.manager.achievements;
      const s = ach.stats;
      s.swarmKills += this.swarmManager.frameKills;
      s.totalKills += this.swarmManager.frameKills;
      for (let t = 1; t <= 5; t++) {
        s.swarmKillsByTier[t as 1 | 2 | 3 | 4 | 5] += this.swarmManager.frameKillsByTier[t]!;
      }
      ach.recheck();
    }

    // Update enemy visibility alpha based on LOS
    this.updateEnemyVisibility(dt);

    // Auto-recall when energy depleted or structure destroyed
    if (!this.deathTransition && (this.robot.energy <= 0 || this.robot.structure <= 0)) {
      const cause = this.robot.structure <= 0 ? 'structure' : 'energy';
      this.startDeathTransition(cause);
    }

    // During death transition, run the transition animation but skip gameplay
    if (this.deathTransition) {
      this.updateDeathTransition(dt);
      return;
    }

    // Update status HUD with bars
    this.drawStatusBars();

    // Update live credits counter in sidebar
    if (this.sidebarCreditsText) {
      this.sidebarCreditsText.text = `Cr  ${this.coinsThisRun}`;
    }

    // Rebuild sidebar when loot bag changes (desktop only — mobile uses MobileLootSidebar)
    if (this.robot.lootBag.length !== this.lastLootCount) {
      this.lastLootCount = this.robot.lootBag.length;
      if (this.vp.mode !== 'mobile') this.buildSidebar();
    }

    // Rebuild combat log when new entries appear — throttled to at most once per 0.1s
    if (this.robot.combatLog.length !== this.lastLogLength) {
      this.lastLogLength = this.robot.combatLog.length;
      this.combatLogDirty = true;
    }
    if (this.combatLogDirty) {
      this.combatLogThrottle += dt;
      if (this.combatLogThrottle >= 0.1) {
        this.combatLogThrottle = 0;
        this.combatLogDirty = false;
        this.buildCombatLog();
      }
    }

    // Rebuild minimap periodically
    this.minimapTimer += dt;
    if (this.minimapTimer >= 0.5) {
      this.minimapTimer = 0;
      this.buildMinimap();
    }
    // Radar sweep overlay — runs every frame for smooth animation
    this.drawRadarOverlay(dt);

    // Screen shake — trigger from new explosion VFX
    for (const v of this.robot.vfx) {
      if ((v.kind === 'explosion' || v.kind === 'death_explosion') && v.life === v.maxLife) {
        // New explosion this frame — intensity scales with radius
        this.shakeIntensity = Math.max(this.shakeIntensity, v.radius * 3);
      }
      if (v.kind === 'robot_hit' && v.life === v.maxLife) {
        // Small shake on robot hit
        this.shakeIntensity = Math.max(this.shakeIntensity, 1.5);
      }
    }
    // Decay shake
    if (this.shakeIntensity > 0) {
      this.shakeIntensity = Math.max(0, this.shakeIntensity - this.shakeDecay * dt);
    }

    // Camera — camWorldX/Y is the tile-coordinate center of the viewport
    if (this.cameraFollowRobot) {
      // Smoothly lerp camera toward robot when following
      const lerpSpeed = 0.15;
      this.camWorldX += (this.robot.displayX - this.camWorldX) * lerpSpeed;
      this.camWorldY += (this.robot.displayY - this.camWorldY) * lerpSpeed;
      const dx = this.robot.displayX - this.camWorldX;
      const dy = this.robot.displayY - this.camWorldY;
      if (dx * dx + dy * dy < 0.0001) {
        this.camWorldX = this.robot.displayX;
        this.camWorldY = this.robot.displayY;
      }
    }

    this.syncAndRenderThree(dt);
  }

  /** Sync all 3D subsystems and render. Safe to call even when 3D is disabled. */
  private syncAndRenderThree(dt: number) {
    if (!this.threeRenderer?.enabled) return;
    this.threeFog?.update(dt, this._frameTime, this.manager.exploration!);
    this.threeRobotRig?.update(this.robot, this._frameTime);
    this.threeEntityMeshes?.syncEntities(this.entities, this._frameTime);
    this.threeSwarmMeshes?.sync(this.swarmManager, this._frameTime);
    this.threeProjectiles?.sync(this.robot.projectiles, this.robot.enemyProjectiles, this._frameTime);
    this.threeVfx?.sync(this.robot.vfx, this.robot.lingerZones);
    this.threeFloatingTexts?.sync(this.robot.floatingTexts, this.zoom);
    this.threeMarker?.sync(this.robot, this._frameTime);
    this.threeRoute?.sync(this.robot, this._frameTime);
    if (this.threeTireTracks) {
      const app = this.manager.app;
      const halfW = app.screen.width  / TILE_SIZE / this.zoom / 2 + 2;
      const halfH = app.screen.height / TILE_SIZE / this.zoom / 2 + 2;
      this.threeTireTracks.update(dt, this.robot, this.camWorldX, this.camWorldY, Math.max(halfW, halfH));
    }
    this.threeRadarBlobs?.sync(this.entities, this.swarmManager);
    if (this.threeLightmap && this.manager.exploration) {
      this.threeLightmap.update(
        this._frameTime,
        this.manager.exploration,
        this.robot.displayX,
        this.robot.displayY,
      );
    }
    this.threeWallPulse?.update(this._frameTime);

    // Camera shake — in 3D, add jitter to world-tile target (conceptually
    // equivalent to the 2D camOffX/Y pixel jitter; world-units shake / TILE
    // yields the same apparent on-screen motion after our perspective math).
    let cx = this.camWorldX;
    let cy = this.camWorldY;
    if (this.shakeIntensity > 0.1) {
      const amt = this.shakeIntensity / TILE_SIZE;
      cx += (Math.random() - 0.5) * amt;
      cy += (Math.random() - 0.5) * amt;
    }
    this.threeRenderer.setCameraTarget(cx, cy, this.zoom);
    this.threeRenderer.render();
  }

  /** Update visAlpha for all enemies based on robot LOS + vision range */
  private updateEnemyVisibility(dt: number) {
    const rx = this.robot.x;
    const ry = this.robot.y;
    const vr = this.robot.visionRadius;
    const vrSq = vr * vr;
    const fadeRate = 1 / ENEMY_VIS_FADE_TIME;

    // Regular entities
    for (const e of this.entities) {
      if (!e.alive) continue;
      if (e.type !== 'enemy') continue;
      const dx = e.x - rx;
      const dy = e.y - ry;
      const distSq = dx * dx + dy * dy;
      if (distSq <= vrSq && this.robot.hasLineOfSight(e.x, e.y)) {
        e.visAlpha = 1;
      } else if (e.visAlpha > 0) {
        e.visAlpha = Math.max(0, e.visAlpha - dt * fadeRate);
      }
    }

    // Swarm enemies
    const sm = this.swarmManager;
    for (let i = 0; i < sm.alive.length; i++) {
      if (!sm.alive[i]) continue;
      const sx = sm.x[i]!;
      const sy = sm.y[i]!;
      const dx = sx - rx;
      const dy = sy - ry;
      const distSq = dx * dx + dy * dy;
      if (distSq <= vrSq && this.robot.hasLineOfSight(sx, sy)) {
        sm.visAlpha[i] = 1;
      } else if (sm.visAlpha[i]! > 0) {
        sm.visAlpha[i] = Math.max(0, sm.visAlpha[i]! - dt * fadeRate);
      }
    }
  }

  // ── Tire tracks ───────────────────────────────────────────────

  /**
   * Draw random debris, stains, and scratches on floor tiles for visual variety.
   * Uses deterministic seeded RNG so debris doesn't change between frames.
   */

  /**
   * Draw drop shadows on floor tiles adjacent to walls.
   * For each wall-floor edge, shadow bands extend from the wall into the floor.
   */

  /**
   * Draw beveled (chamfered) outer corners on wall tiles.
   * An outer corner exists where two adjacent cardinal neighbors are walkable (floor).
   */

  /**
   * Draw ¾-view south-facing wall front faces.
   * For each wall tile with a walkable tile below it, draw a short vertical face
   * that overlaps the top of the floor tile, giving walls visible height.
   */

  /**
   * Transform a local tile coordinate (relative to tile top-left) by the wall's rotation.
   * Returns world coordinates.
   */


  // -------------------------------------------------------
  // Lighting — darkness overlay with additive light sources
  // -------------------------------------------------------

  // -------------------------------------------------------
  // Status HUD — backdrop panel + bars for HP, Energy, XP with header labels
  // -------------------------------------------------------
  private drawStatusBars() {
    // Round to one decimal; drop the trailing ".0" so integer values stay
    // clean. Upgrades apply fractional score multipliers to maxEnergy, so
    // without this the bar label can spill across the whole bar.
    const fmt1 = (x: number): string => {
      const r = Math.round(x * 10) / 10;
      return Number.isInteger(r) ? r.toFixed(0) : r.toFixed(1);
    };
    const g = this.statusBarGraphics;
    g.clear();

    const robot = this.robot;
    const stats = this.manager.stats;

    // Layout constants (must match init hit-area setup)
    const BAR_W = 210;
    const BAR_H = 24;
    const BAR_GAP = 8;
    const HUD_PAD = 10;
    const LABEL_H = 14;
    const LABEL_GAP = 3;
    const BAR_R = 4;

    const barsX = HUD_PAD;
    const labelY = HUD_PAD;
    const barY = labelY + LABEL_H + LABEL_GAP;

    // ── Backdrop panel ──
    const totalBarsW = 3 * BAR_W + 2 * BAR_GAP;
    const backdropW = totalBarsW + HUD_PAD * 2;
    const backdropH = HUD_PAD + LABEL_H + LABEL_GAP + BAR_H + HUD_PAD;
    drawBevelPanel(g, 0, 0, backdropW, backdropH, {
      fill: 0x0a0e14, border: ACCENT_DIM, radius: 6, alpha: 0.88,
    });

    // Bar definitions
    // Compute live XP preview (pending xpGained not yet applied to stats)
    const previewTotalXp = stats.totalXp + robot.xpGained;
    let previewLevel = stats.level;
    while (previewTotalXp >= xpForLevel(previewLevel + 1)) previewLevel++;
    const curLevelXp = xpForLevel(previewLevel);
    const nextLevelXp = xpForLevel(previewLevel + 1);
    const xpRange = nextLevelXp - curLevelXp;
    const previewXpProgress = xpRange > 0 ? (previewTotalXp - curLevelXp) / xpRange : 1;

    const barDefs = [
      { header: 'HP', frac: robot.structure / robot.maxStructure,
        text: `${Math.ceil(robot.structure)} / ${robot.maxStructure}`,
        color: 0x44cc66, headerColor: 0x55dd77 },
      { header: 'ENERGY', frac: robot.energy / robot.maxEnergy,
        text: `${fmt1(robot.energy)} / ${fmt1(robot.maxEnergy)}`,
        color: 0xffaa22, headerColor: 0xffbb44 },
      { header: `LEVEL ${previewLevel}`, frac: previewXpProgress,
        text: `+${robot.xpGained} XP`,
        color: 0x44aaff, headerColor: 0x66bbff },
    ];

    // Reset label tracking
    this.barLabels = [];

    for (let i = 0; i < barDefs.length; i++) {
      const def = barDefs[i]!;
      const xPos = barsX + i * (BAR_W + BAR_GAP);
      const clamped = Math.max(0, Math.min(1, def.frac));

      // ── Header label above bar ──
      let hdr = this.barHeaderLabels[i];
      if (!hdr) {
        hdr = new Text({
          text: '',
          style: { fontFamily: 'Gugi', fontSize: 14, fill: 0xaa6677, dropShadow: GUGI_SHADOW },
        });
        this.statusBarContainer.addChild(hdr);
        this.barHeaderLabels[i] = hdr;
      }
      hdr.text = def.header;
      hdr.style.fill = def.headerColor;
      hdr.position.set(xPos + 4, labelY);
      hdr.visible = true;

      // ── Percentage right-aligned in header row ──
      const pctStr = `${Math.round(clamped * 100)}%`;
      let pctTxt = this.barHeaderLabels[3 + i]; // indices 3-5 for pct labels
      if (!pctTxt) {
        pctTxt = new Text({
          text: '',
          style: { fontFamily: 'Offside', fontSize: 13, fill: 0x667788 },
        });
        this.statusBarContainer.addChild(pctTxt);
        this.barHeaderLabels[3 + i] = pctTxt;
      }
      pctTxt.text = pctStr;
      pctTxt.style.fill = clamped < 0.25 && def.color !== 0x44aaff ? 0xff6644 : 0x667788;
      pctTxt.position.set(xPos + BAR_W - pctTxt.width - 4, labelY + 2);
      pctTxt.visible = true;

      // ── Status bar fill ──
      drawStatusBar(g, xPos, barY, BAR_W, BAR_H, BAR_R, 0x0a0e14, def.color, clamped, ACCENT_DIM, 0.35);

      // Low warning flash overlay
      if (clamped < 0.25 && def.color !== 0x44aaff) {
        const flash = 0.5 + Math.sin(this._frameTime * 8) * 0.3;
        g.roundRect(xPos, barY, Math.max(BAR_R * 2, BAR_W * clamped), BAR_H, BAR_R);
        g.fill({ color: 0xff2200, alpha: flash * 0.6 });
      }

      // ── Value text centered inside bar ──
      const labelIdx = this.barLabels.length;
      let txt = this.barLabelPool[labelIdx];
      if (!txt) {
        txt = new Text({
          text: '',
          style: {
            fontFamily: 'Offside', fontSize: 16, fill: 0xffffff,
            dropShadow: { color: 0x000000, blur: 2, distance: 1, alpha: 0.85 },
          },
        });
        this.statusBarContainer.addChild(txt);
        this.barLabelPool.push(txt);
      }
      txt.text = def.text;
      txt.visible = true;
      txt.position.set(xPos + BAR_W / 2 - txt.width / 2, barY + (BAR_H - 14) / 2);
      this.barLabels.push(txt);
    }

    // ── State badge with pill background ──
    const stateLabel = robot.state === 'exploring' ? 'EXPLORING'
      : robot.state === 'combat' ? 'COMBAT!'
      : robot.state === 'looting' ? 'LOOTING...'
      : 'MOVING';
    const stateColor = robot.state === 'combat' ? 0xff6644
      : robot.state === 'looting' ? 0xffcc44
      : 0x4488ff;
    const stateBgColor = robot.state === 'combat' ? 0x331111
      : robot.state === 'looting' ? 0x2a2211
      : 0x112233;

    let rightText = stateLabel;
    if (robot.passive) rightText += '  [PASSIVE]';
    const lootCount = robot.lootBag.length;
    if (lootCount > 0) rightText += `  |  Loot: ${lootCount}`;
    if (robot.state === 'combat' && robot.target) {
      const enemy = robot.target as Enemy;
      rightText += `  [${Math.ceil(enemy.hp)}/${enemy.maxHp}]`;
    }
    if (robot.state === 'looting') {
      rightText += `  [${Math.round(robot.lootProgress * 100)}%]`;
    }

    const totalSec = Math.floor(this.elapsedTime);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    rightText += `  |  ${min}:${sec.toString().padStart(2, '0')}`;

    this.statusText.text = rightText;
    this.statusText.style.fill = stateColor;

    // Position status badge — right of bars on desktop, below them on mobile
    // (the backdrop is only as wide as the bars, so a right-side badge would
    // overflow the scaled mobile HUD).
    const PILL_PAD_X = 14;
    const PILL_PAD_Y = 6;
    const isMobileHud = this.vp.mode === 'mobile';
    const textH = this.statusText.height;
    const pillW = this.statusText.width + PILL_PAD_X * 2;
    const pillH = textH + PILL_PAD_Y * 2;
    const badgeX = isMobileHud ? 0 : backdropW + 6;
    const badgeY = isMobileHud ? backdropH + 4 : barY;

    // Pill background
    g.roundRect(badgeX, badgeY, pillW, pillH, BAR_R);
    g.fill({ color: stateBgColor, alpha: 0.9 });
    g.roundRect(badgeX, badgeY, pillW, pillH, BAR_R);
    g.stroke({ color: stateColor, width: 1, alpha: 0.3 });

    // Combat pulse border
    if (robot.state === 'combat') {
      const pulse = 0.15 + Math.sin(this._frameTime * 6) * 0.15;
      g.roundRect(badgeX, badgeY, pillW, pillH, BAR_R);
      g.stroke({ color: 0xff4422, width: 1.5, alpha: pulse });
    }

    this.statusText.position.set(badgeX + PILL_PAD_X, badgeY + PILL_PAD_Y);

    // Hide unused pooled labels
    for (let i = this.barLabels.length; i < this.barLabelPool.length; i++) {
      this.barLabelPool[i]!.visible = false;
    }
  }

  // ── Simple button tooltip helper ────────────────────────
  private addButtonTooltip(btn: Container, getText: () => string, btnSize: number) {
    const tip = new Container();
    tip.visible = false;
    tip.eventMode = 'none';
    tip.interactiveChildren = false;

    const tipBg = new Graphics();
    tip.addChild(tipBg);

    const tipText = new Text({
      text: '',
      style: { fontFamily: 'Offside', fontSize: 11, fill: 0xccddee },
    });
    tipText.position.set(6, 4);
    tip.addChild(tipText);

    this.uiContainer.addChild(tip);

    btn.on('pointerenter', () => {
      tipText.text = getText();
      const w = tipText.width + 12;
      const h = tipText.height + 8;
      tipBg.clear();
      tipBg.roundRect(0, 0, w, h, 4);
      tipBg.fill({ color: 0x0a0e14, alpha: 0.94 });
      tipBg.stroke({ color: 0x445566, width: 1 });
      // Position below the button, right-aligned so it doesn't overflow right edge
      tip.position.set(btn.x + btnSize - w, btn.y + btnSize + 4);
      tip.visible = true;
    });
    btn.on('pointerleave', () => { tip.visible = false; });
  }

  // ── Stance toggle button (passive / aggressive) ───────────
  private buildStanceButton(x: number, y: number) {
    const btn = new Container();
    btn.position.set(x, y);

    const bg = new Graphics();
    btn.addChild(bg);

    const icon = new Graphics();
    btn.addChild(icon);

    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    btn.on('pointerdown', () => {
      this.robot.passive = !this.robot.passive;
      // If toggling to passive while in combat, the robot will disengage next frame
      this.updateStanceButton();
    });

    this.stanceBtnBg = bg;
    this.stanceBtnIcon = icon;
    this.uiContainer.addChild(btn);
    this.updateStanceButton();

    this.addButtonTooltip(btn, () =>
      this.robot.passive
        ? 'Passive — avoids combat'
        : 'Aggressive — engages enemies',
      36);
  }

  private updateStanceButton() {
    const SIZE = 36;
    const bg = this.stanceBtnBg!;
    const icon = this.stanceBtnIcon!;
    const passive = this.robot.passive;

    // Background
    bg.clear();
    bg.roundRect(0, 0, SIZE, SIZE, 5);
    bg.fill({ color: passive ? 0x112233 : 0x221111, alpha: 0.92 });
    bg.stroke({ color: passive ? 0x4488aa : 0xaa4444, width: 1 });

    // Icon
    icon.clear();
    const cx = SIZE / 2;
    const cy = SIZE / 2;
    if (passive) {
      // Shield icon — peaceful / passive
      // Simple shield shape
      icon.moveTo(cx, cy - 10);
      icon.lineTo(cx + 8, cy - 5);
      icon.lineTo(cx + 8, cy + 2);
      icon.quadraticCurveTo(cx, cy + 12, cx, cy + 12);
      icon.quadraticCurveTo(cx, cy + 12, cx - 8, cy + 2);
      icon.lineTo(cx - 8, cy - 5);
      icon.closePath();
      icon.stroke({ color: 0x4488cc, width: 1.5 });
      icon.fill({ color: 0x4488cc, alpha: 0.15 });
    } else {
      // Crosshair icon — aggressive / combat
      icon.circle(cx, cy, 7);
      icon.stroke({ color: 0xff5544, width: 1.5 });
      // Cross lines
      icon.moveTo(cx, cy - 10).lineTo(cx, cy - 4);
      icon.moveTo(cx, cy + 4).lineTo(cx, cy + 10);
      icon.moveTo(cx - 10, cy).lineTo(cx - 4, cy);
      icon.moveTo(cx + 4, cy).lineTo(cx + 10, cy);
      icon.stroke({ color: 0xff5544, width: 1.5 });
      // Center dot
      icon.circle(cx, cy, 1.5);
      icon.fill(0xff5544);
    }
  }

  // ── Follow-robot camera button ─────────────────────────
  private buildFollowButton(x: number, y: number) {
    const btn = new Container();
    btn.position.set(x, y);

    const bg = new Graphics();
    btn.addChild(bg);

    const icon = new Graphics();
    btn.addChild(icon);

    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    btn.on('pointerdown', () => {
      this.cameraFollowRobot = true;
      this.updateFollowButton();
    });

    this.followBtnBg = bg;
    this.followBtnIcon = icon;
    this.uiContainer.addChild(btn);
    this.updateFollowButton();

    this.addButtonTooltip(btn, () =>
      this.cameraFollowRobot
        ? 'Camera tracking robot'
        : 'Click to follow robot',
      36);
  }

  private updateFollowButton() {
    const SIZE = 36;
    const bg = this.followBtnBg!;
    const icon = this.followBtnIcon!;
    const following = this.cameraFollowRobot;

    // Background
    bg.clear();
    bg.roundRect(0, 0, SIZE, SIZE, 5);
    bg.fill({ color: following ? 0x112233 : 0x332211, alpha: 0.92 });
    bg.stroke({ color: following ? 0x4488aa : 0xaa8844, width: 1 });

    // Icon — crosshair/target reticle centered on a dot (robot)
    icon.clear();
    const cx = SIZE / 2;
    const cy = SIZE / 2;

    const col = following ? 0x4488cc : 0xddaa44;

    // Outer circle
    icon.circle(cx, cy, 10);
    icon.stroke({ color: col, width: 1.2 });

    // Cross lines (with gap in center)
    icon.moveTo(cx, cy - 10).lineTo(cx, cy - 4);
    icon.moveTo(cx, cy + 4).lineTo(cx, cy + 10);
    icon.moveTo(cx - 10, cy).lineTo(cx - 4, cy);
    icon.moveTo(cx + 4, cy).lineTo(cx + 10, cy);
    icon.stroke({ color: col, width: 1.2 });

    // Center dot (robot)
    icon.circle(cx, cy, 2);
    icon.fill(col);
  }

  /** Acquire a Graphics from the fade pool (or create a new one) */

  /** Return all active fade Graphics to the pool */

  /** Draw faint green circular blobs on the main map for radar-pinged enemies */


  // -------------------------------------------------------
  // Projectiles & VFX
  // -------------------------------------------------------

  // -------------------------------------------------------
  // Fog of war — textured sprites with blur and fade-out
  // -------------------------------------------------------

  /** Destroy all fog sprites — used when switching to flat LOD */



  // -------------------------------------------------------
  // Sidebar — equipped items + loot bag
  // -------------------------------------------------------
  private buildSidebar() {
    // Destroy old children & tooltip
    this.hideSidebarTooltip();
    for (const child of this.sidebarContainer.removeChildren()) {
      child.destroy({ children: true });
    }

    const app = this.manager.app;
    const ups = this.manager.upgrades;
    const cellSize = 40;
    const cellGap = 3;
    const cols = 5;
    const panelW = cols * (cellSize + cellGap) + cellGap + 12;
    const panelX = app.screen.width - panelW - 10;
    let py = 64; // below recall button (10 + 44 + 10 margin)
    const headerStyle = { fontFamily: 'Gugi', fontSize: 13, fill: 0xaa6677, dropShadow: GUGI_SHADOW } as const;
    const drawSidebarHeader = (text: string, y: number) => {
      const band = new Graphics();
      drawHeaderBand(band, panelX - 6, y - 2, panelW + 12, 0x1a0a0d, 0x0a0e14, 22);
      this.sidebarContainer.addChild(band);
      const t = new Text({ text, style: headerStyle });
      t.position.set(panelX, y);
      this.sidebarContainer.addChild(t);
    };

    // Panel background
    const bg = new Graphics();
    drawBevelPanel(bg, panelX - 6, py - 6, panelW + 12, app.screen.height - py - 10, { fill: 0x0a0e14, border: ACCENT_DIM, radius: 4, alpha: 0.75 });
    this.sidebarContainer.addChild(bg);

    // Credits earned this run — updated live in update()
    const creditsText = new Text({
      text: `Cr  ${this.coinsThisRun}`,
      style: { fontFamily: 'Offside', fontSize: 13, fill: 0xffdd44 },
    });
    creditsText.position.set(panelX, py + 2);
    this.sidebarContainer.addChild(creditsText);
    this.sidebarCreditsText = creditsText;
    py += 22;

    const addCellWithTooltip = (
      cell: Container, cx: number, cy: number,
      tipFactory: () => { tip: Container; width: number; height: number },
    ) => {
      cell.position.set(cx, cy);
      cell.eventMode = 'static';
      cell.cursor = 'pointer';
      cell.on('pointerenter', () => {
        this.hideSidebarTooltip();
        const { tip, width, height } = tipFactory();
        positionTooltip(tip, height, width, cx, cy, app.screen.width, app.screen.height, cellSize);
        this.sidebarContainer.addChild(tip);
        this.sidebarTooltip = tip;
      });
      cell.on('pointerleave', () => this.hideSidebarTooltip());
      this.sidebarContainer.addChild(cell);
    };

    // ── Equipped Weapons ──
    const weapons = ups.getEquippedWeapons();
    const hasWeapons = weapons.some(w => w !== null);

    if (hasWeapons) {
      drawSidebarHeader('WEAPONS', py);
      py += 20;

      let col = 0;
      for (let i = 0; i < weapons.length; i++) {
        const w = weapons[i];
        if (!w) continue;
        const cx = panelX + col * (cellSize + cellGap);
        const cell = buildWeaponCell(w.def, w.item, cellSize);
        addCellWithTooltip(cell, cx, py, () => createWeaponTooltip(w.def, w.item));
        col++;
        if (col >= cols) { col = 0; py += cellSize + cellGap; }
      }
      if (col > 0) py += cellSize + cellGap;
      py += 4;
    }

    // ── Equipped Upgrades ──
    const cats = ['engine', 'sensors', 'capacitor', 'shield', 'chassis'] as const;
    const equippedUpgrades = cats.map(c => ({ cat: c, eq: ups.getEquipped(c) })).filter(e => e.eq);

    if (equippedUpgrades.length > 0) {
      drawSidebarHeader('EQUIPMENT', py);
      py += 20;

      let col = 0;
      for (const { eq } of equippedUpgrades) {
        if (!eq) continue;
        const cx = panelX + col * (cellSize + cellGap);
        const cell = buildUpgradeCell(eq.def, eq.item, cellSize);
        addCellWithTooltip(cell, cx, py, () => createUpgradeTooltip(eq.def, eq.item));
        col++;
        if (col >= cols) { col = 0; py += cellSize + cellGap; }
      }
      if (col > 0) py += cellSize + cellGap;
      py += 4;
    }

    // ── Loot Bag ──
    const lootBag = this.robot.lootBag;
    if (lootBag.length > 0) {
      // Divider
      const div = new Graphics();
      div.moveTo(panelX, py);
      div.lineTo(panelX + panelW, py);
      div.stroke({ color: ACCENT_DIM, width: 1, alpha: 0.4 });
      this.sidebarContainer.addChild(div);
      py += 6;

      drawSidebarHeader(`LOOT BAG (${lootBag.length})`, py);
      py += 20;

      let col = 0;
      for (const loot of lootBag) {
        const cx = panelX + col * (cellSize + cellGap);
        const isWeapon = loot.def.kind === 'weapon';
        const cell = isWeapon
          ? buildWeaponCell(loot.def as WeaponDef, loot.item, cellSize)
          : buildUpgradeCell(loot.def as UpgradeDef, loot.item, cellSize);
        const tipFactory = isWeapon
          ? () => createWeaponTooltip(loot.def as WeaponDef, loot.item)
          : () => createUpgradeTooltip(loot.def as UpgradeDef, loot.item);
        addCellWithTooltip(cell, cx, py, tipFactory);
        col++;
        if (col >= cols) { col = 0; py += cellSize + cellGap; }
      }
    }
  }

  private hideSidebarTooltip() {
    if (this.sidebarTooltip) {
      this.sidebarContainer.removeChild(this.sidebarTooltip);
      this.sidebarTooltip.destroy({ children: true });
      this.sidebarTooltip = null;
    }
  }

  // -------------------------------------------------------
  // Floating damage/status texts
  // -------------------------------------------------------

  // -------------------------------------------------------
  // Combat Log — bottom-left event feed (desktop) / top strip (mobile)
  // -------------------------------------------------------
  private buildCombatLog() {
    const isMobile = this.vp.mode === 'mobile';

    // Rebuild persistent objects on mode flip
    if (this.combatLogMobileMode !== isMobile) {
      this.combatLogBg?.destroy(); this.combatLogBg = null;
      this.combatLogHeader?.destroy(); this.combatLogHeader = null;
      for (const t of this.combatLogTexts) t.destroy();
      this.combatLogTexts = [];
      this.combatLogScrollDrag?.destroy(); this.combatLogScrollDrag = null;
      this.combatLogContent = null;
      this.combatLogScrollY = 0;
      this.combatLogAutoFollow = true;
      this.combatLogContainer.removeChildren();
      this.combatLogMobileMode = isMobile;
    }

    const UI_MARGIN = 10;
    let maxVisible: number, lineH: number, fontSize: number;
    let panelX: number, panelY: number, panelW: number, panelH: number;
    let showHeader: boolean;

    if (isMobile) {
      maxVisible = 3;
      lineH = 16;
      fontSize = 11;
      showHeader = false;
      panelX = UI_MARGIN;
      panelW = this.vp.w - UI_MARGIN * 2;
      const DESKTOP_BACKDROP_W = 3 * 210 + 2 * 8 + 10 * 2;
      const hudScale = Math.min(1, (this.vp.w - 20) / DESKTOP_BACKDROP_W);
      panelY = UI_MARGIN + Math.ceil(93 * hudScale) + UI_MARGIN + this.vp.safeTop;
      panelH = maxVisible * lineH + 12;
    } else {
      maxVisible = 14;
      lineH = 20;
      fontSize = 13;
      showHeader = true;
      panelW = 400;
      panelX = UI_MARGIN;
      panelH = maxVisible * lineH + 34;
      panelY = UI_MARGIN + 61 + UI_MARGIN;
    }

    // Background — reuse persistent Graphics
    if (!this.combatLogBg) {
      this.combatLogBg = new Graphics();
      drawBevelPanel(this.combatLogBg, panelX, panelY, panelW, panelH, { fill: 0x0a0e14, border: ACCENT_DIM, radius: 4, alpha: 0.7 });
      this.combatLogContainer.addChild(this.combatLogBg);
    }

    // Header — desktop only
    if (showHeader && !this.combatLogHeader) {
      const logBand = new Graphics();
      drawHeaderBand(logBand, panelX, panelY, panelW, 0x1a0a0d, 0x0a0e14, 22);
      this.combatLogContainer.addChild(logBand);
      this.combatLogHeader = new Text({ text: 'LOG', style: { fontFamily: 'Gugi', fontSize: 13, fill: 0xaa6677, dropShadow: GUGI_SHADOW } });
      this.combatLogHeader.position.set(panelX + 4, panelY + 4);
      this.combatLogContainer.addChild(this.combatLogHeader);
    }

    const textTopY = panelY + (showHeader ? 24 : 6);
    const viewH = maxVisible * lineH;

    // Masked, scrollable content container — created once per mode.
    if (!this.combatLogContent) {
      this.combatLogContent = new Container();
      this.combatLogContent.eventMode = 'static';
      this.combatLogContainer.addChild(this.combatLogContent);

      const mask = new Graphics();
      mask.rect(panelX, textTopY, panelW, viewH);
      mask.fill(0xffffff);
      this.combatLogContainer.addChild(mask);
      this.combatLogContent.mask = mask;

      this.combatLogScrollDrag = attachScrollDrag({
        hitArea: this.combatLogContent,
        onDelta: (dy: number) => {
          if (this.combatLogMaxScroll <= 0) return;
          this.combatLogScrollY = Math.max(0, Math.min(this.combatLogMaxScroll, this.combatLogScrollY + dy));
          // Auto-follow re-engages when user scrolls back to the bottom.
          this.combatLogAutoFollow = this.combatLogScrollY >= this.combatLogMaxScroll - 0.5;
          this.applyCombatLogScroll();
        },
      });
    }

    // Log content is always sized to the visible viewport + scrollable history.
    const log = this.robot.combatLog;
    const totalH = log.length * lineH;
    const prevMaxScroll = this.combatLogMaxScroll;
    const prevAtBottom = this.combatLogAutoFollow || this.combatLogScrollY >= prevMaxScroll - 0.5;
    this.combatLogMaxScroll = Math.max(0, totalH - viewH);

    // Grow/shrink the Text pool to match log length.
    for (let i = 0; i < log.length; i++) {
      let txt = this.combatLogTexts[i];
      if (!txt) {
        txt = new Text({ text: '', style: { fontFamily: 'Offside', fontSize, fill: 0xffffff } });
        this.combatLogTexts[i] = txt;
        this.combatLogContent.addChild(txt);
      }
      const entry = log[i]!;
      txt.text = entry.text;
      txt.style.fill = entry.color;
      txt.alpha = 1;
      txt.position.set(panelX + 8, textTopY + i * lineH);
      txt.visible = true;
    }
    // Hide any stale pooled texts beyond current log length (shouldn't happen
    // with the shift-based log, but keeps the pool safe on mode flips).
    for (let i = log.length; i < this.combatLogTexts.length; i++) {
      const t = this.combatLogTexts[i];
      if (t) t.visible = false;
    }

    // If the user was at the bottom (or auto-follow is on), track the newest.
    if (prevAtBottom) {
      this.combatLogScrollY = this.combatLogMaxScroll;
      this.combatLogAutoFollow = true;
    } else {
      this.combatLogScrollY = Math.min(this.combatLogScrollY, this.combatLogMaxScroll);
    }
    this.applyCombatLogScroll();
  }

  private applyCombatLogScroll() {
    if (!this.combatLogContent) return;
    this.combatLogContent.y = -this.combatLogScrollY;
  }


  private drawBuildings() {
    const bs = this.manager.buildings;
    rebuildBuildingSprites(this.buildingSpriteContainer, bs.buildings, bs.baseOriginX, bs.baseOriginY, this.buildingTextures);
    rebuildBuildingLabels(this.buildingLabelContainer, bs.buildings, bs.baseOriginX, bs.baseOriginY);
  }


  /** Tiles newly revealed this deployment */
  private get coinsThisRun(): number {
    return (this.manager.exploration?.revealedCount ?? 0) - this.revealedAtRunStart;
  }

  // ─── Death Transition ───────────────────────────────────────────────

  /** Begin the death transition sequence — freezes gameplay and plays a cinematic death */
  private startDeathTransition(cause: 'structure' | 'energy') {
    this.deathTransition = true;
    this.deathTransitionTime = 0;
    this.deathTransitionCause = cause;
    this.deathZoomStart = this.zoom;

    // Big initial screen shake
    this.shakeIntensity = LabyrinthState.DEATH_SHAKE_INITIAL;

    // Stop motor sound
    this.motorSound?.stop();

    // Play explosion sound for structural death
    if (cause === 'structure') {
      void this.manager.sound?.playSound('/sound/explosion.mp3', 2.5);
    }

    // Fade out the music
    this.manager.sound?.fadeOutMusic(2.0);

    // Generate random scanline/static data for the interference effect
    this.deathStaticLines = [];
    for (let i = 0; i < 30; i++) {
      this.deathStaticLines.push({
        y: Math.random(),
        speed: (Math.random() - 0.5) * 0.4,
        alpha: 0.15 + Math.random() * 0.25,
      });
    }

    // Create overlay container (added to main container, above everything)
    const app = this.manager.app;
    this.deathOverlay = new Graphics();
    this.container.addChild(this.deathOverlay);

    // "SIGNAL LOST" / "STRUCTURAL FAILURE" text
    const headerText = cause === 'structure' ? 'STRUCTURAL FAILURE' : 'SIGNAL LOST';
    this.deathText = new Text({
      text: headerText,
      style: {
        fontFamily: 'Gugi',
        fontSize: 52,
        fill: cause === 'structure' ? 0xff4444 : 0xffaa22,
        dropShadow: { ...GUGI_SHADOW, distance: 4 },
        letterSpacing: 6,
      },
    });
    this.deathText.anchor.set(0.5);
    this.deathText.position.set(app.screen.width / 2, app.screen.height / 2 - 20);
    this.deathText.alpha = 0;
    this.container.addChild(this.deathText);

    // Sub-text
    this.deathSubText = new Text({
      text: cause === 'structure' ? 'Hull integrity compromised' : 'Power reserves depleted',
      style: {
        fontFamily: 'Gugi',
        fontSize: 20,
        fill: 0x999999,
        dropShadow: { ...GUGI_SHADOW, distance: 2 },
        letterSpacing: 3,
      },
    });
    this.deathSubText.anchor.set(0.5);
    this.deathSubText.position.set(app.screen.width / 2, app.screen.height / 2 + 30);
    this.deathSubText.alpha = 0;
    this.container.addChild(this.deathSubText);
  }

  /** Animate the death transition each frame — handles overlay, zoom, shake, and final switch */
  private updateDeathTransition(dt: number) {
    this.deathTransitionTime += dt;
    const t = this.deathTransitionTime;
    const duration = LabyrinthState.DEATH_DURATION;
    const progress = Math.min(t / duration, 1);
    const app = this.manager.app;
    const w = app.screen.width;
    const h = app.screen.height;

    // Phase timing:
    // 0.0 - 0.3s: initial shake + explosion flash
    // 0.3 - 1.2s: UI fades out, zoom begins, overlay darkens
    // 1.2 - 2.2s: text appears with glitch, static interference
    // 2.2 - 3.0s: full darken, transition out

    // ── Shake decay (slower than normal combat shake) ──
    if (this.shakeIntensity > 0) {
      this.shakeIntensity = Math.max(0, this.shakeIntensity - 4 * dt);
    }

    // ── Camera: keep following robot, slow zoom in ──
    this.mapContainer.scale.set(this.zoom);
    const targetZoom = Math.min(MAX_ZOOM, this.deathZoomStart + LabyrinthState.DEATH_ZOOM_SPEED * t);
    this.zoom = targetZoom;
    this.camWorldX += (this.robot.displayX - this.camWorldX) * 0.05;
    this.camWorldY += (this.robot.displayY - this.camWorldY) * 0.05;
    let camOffX = w / 2 - this.camWorldX * TILE_SIZE * this.zoom;
    let camOffY = h / 2 - this.camWorldY * TILE_SIZE * this.zoom;
    if (this.shakeIntensity > 0.1) {
      camOffX += (Math.random() - 0.5) * this.shakeIntensity * this.zoom;
      camOffY += (Math.random() - 0.5) * this.shakeIntensity * this.zoom;
    }
    this.mapContainer.x = camOffX;
    this.mapContainer.y = camOffY;

    // ── Fade out HUD ──
    const hudFade = Math.max(0, 1 - t / 0.8);
    this.uiContainer.alpha = hudFade;

    // ── Map desaturation simulation: darken the map container ──
    const mapDarken = Math.min(0.6, t * 0.3);
    this.mapContainer.alpha = 1 - mapDarken;

    // ── Draw overlay ──
    if (this.deathOverlay) {
      this.deathOverlay.clear();

      // Base dark overlay — grows from edges (vignette)
      const overlayAlpha = Math.min(0.85, progress * 0.9);
      const tintColor = this.deathTransitionCause === 'structure' ? 0x1a0000 : 0x1a0c00;
      this.deathOverlay.rect(0, 0, w, h).fill({ color: tintColor, alpha: overlayAlpha });

      // Vignette effect — darker at edges
      const vignetteAlpha = Math.min(0.5, progress * 0.6);
      // Top/bottom bands
      this.deathOverlay.rect(0, 0, w, h * 0.15).fill({ color: 0x000000, alpha: vignetteAlpha });
      this.deathOverlay.rect(0, h * 0.85, w, h * 0.15).fill({ color: 0x000000, alpha: vignetteAlpha });

      // Scanline/static interference effect
      if (t > 0.2) {
        const staticAlpha = Math.min(1, (t - 0.2) / 0.5);
        for (const line of this.deathStaticLines) {
          line.y += line.speed * dt;
          if (line.y > 1) line.y -= 1;
          if (line.y < 0) line.y += 1;
          const ly = line.y * h;
          const lh = 1 + Math.random() * 3;
          this.deathOverlay.rect(0, ly, w, lh)
            .fill({ color: 0xffffff, alpha: line.alpha * staticAlpha * (0.3 + 0.7 * Math.random()) });
        }
      }

      // Flash on structural death (brief white flash at start)
      if (this.deathTransitionCause === 'structure' && t < 0.15) {
        const flashAlpha = (1 - t / 0.15) * 0.6;
        this.deathOverlay.rect(0, 0, w, h).fill({ color: 0xffffff, alpha: flashAlpha });
      }

      // Horizontal glitch bars (random displacement effect)
      if (t > 0.5 && t < 2.5) {
        const glitchIntensity = Math.sin(t * 8) * 0.5 + 0.5;
        if (Math.random() < 0.3 * glitchIntensity) {
          const gy = Math.random() * h;
          const gh = 2 + Math.random() * 6;
          const gx = (Math.random() - 0.5) * 20;
          this.deathOverlay.rect(gx, gy, w, gh)
            .fill({ color: this.deathTransitionCause === 'structure' ? 0xff2222 : 0xffaa00, alpha: 0.15 });
        }
      }
    }

    // ── Text fade-in (appears around t=1.0) ──
    if (this.deathText) {
      const textProgress = Math.max(0, (t - 0.8) / 0.6);
      this.deathText.alpha = Math.min(1, textProgress);
      // Subtle glitch offset
      if (t > 0.8 && t < 2.0 && Math.random() < 0.1) {
        this.deathText.x = w / 2 + (Math.random() - 0.5) * 8;
      } else {
        this.deathText.x = w / 2;
      }
    }
    if (this.deathSubText) {
      const subProgress = Math.max(0, (t - 1.2) / 0.6);
      this.deathSubText.alpha = Math.min(1, subProgress);
    }

    // Keep the 3D view in sync during the transition (zoom / shake / VFX).
    this.syncAndRenderThree(dt);

    // ── Final switch ──
    if (t >= duration) {
      this.endRun(this.deathTransitionCause);
    }
  }

  /** Show end-of-run results screen (energy or structure depleted) */
  private endRun(cause: 'structure' | 'energy') {
    const coinsEarned = Math.floor(this.coinsThisRun * this.manager.buildings.getCreditMultiplier());
    this.commitRunEndStats(coinsEarned, false);
    this.manager.switchTo(new EndRunState(this.manager, {
      cause,
      xpGained: this.robot.xpGained,
      killCount: this.robot.killCount,
      lootBag: [...this.robot.lootBag],
      coinsEarned,
    }));
  }

  /** Update PersistentStats with per-run maxes and lifetime totals at end-of-run.
   *  Called from both the death path (`endRun`) and the manual recall path
   *  (`transferXpAndRecall`) so a run's stats are always committed exactly once. */
  private commitRunEndStats(coinsEarned: number, recalled: boolean) {
    const ach = this.manager.achievements;
    const s = ach.stats;
    s.totalDeployments++;
    s.runsCompleted++;
    if (recalled) s.runsRecalled++;
    else s.totalDeaths++;
    if (this.robot.killCount > s.bestRunKills) s.bestRunKills = this.robot.killCount;
    if (this.robot.xpGained > s.bestRunXp) s.bestRunXp = this.robot.xpGained;
    if (coinsEarned > s.bestRunCoins) s.bestRunCoins = coinsEarned;
    s.moneyEarnedTotal += coinsEarned;
    const lootCount = this.robot.lootBag.length;
    if (lootCount > s.bestRunLootCount) s.bestRunLootCount = lootCount;
    let runLegendary = 0;
    for (const loot of this.robot.lootBag) {
      if (loot.item.score >= 96) runLegendary++;
    }
    if (runLegendary > s.bestRunLegendaryCount) s.bestRunLegendaryCount = runLegendary;

    // Duration + distance.
    const duration = performance.now() / 1000 - this._deployStartSec;
    if (duration > s.bestRunDuration) s.bestRunDuration = duration;
    if (this.robot.maxDistFromSpawnThisRun > s.bestRunDistanceFromSpawn) {
      s.bestRunDistanceFromSpawn = this.robot.maxDistFromSpawnThisRun;
    }

    // Exploration delta + per-run %.
    const exploration = this.manager.exploration;
    const lab = this.labyrinth;
    if (exploration && lab) {
      const delta = Math.max(0, exploration.revealedCount - this._tilesRevealedAtStart);
      s.tilesRevealedTotal += delta;
      const walkable = lab.walkableCount;
      const pct = walkable > 0 ? Math.floor((exploration.visited.size / walkable) * 100) : 0;
      if (pct > s.bestExplorationPercent) s.bestExplorationPercent = pct;
      if (pct >= 100) {
        s.perfectRunCount++;
        const mapSize = lab.width * lab.height;
        if (mapSize > s.maxMapFullyExplored) s.maxMapFullyExplored = mapSize;
      }
    }

    // Damage-shape flags. Only credited on successful recalls — dying with
    // zero damage taken is only possible through trivial exploits and isn't
    // what Untouchable means.
    if (recalled) {
      if (this.robot.damageTakenThisRun === 0) {
        s.flawlessRun++;
        s.noDamageRuns++;
      }
      if (this.robot.lowHpEverThisRun) s.comebackRuns++;
      if (this.robot.structure < this.robot.maxStructure * 0.1) s.lowHpClears++;
    }

    ach.recheck();
    // Re-sync derived context (level/stat/completion%) with freshly committed totals.
    this.manager.recomputeAchievementContext();
  }

  /** Transfer earned XP, loot, and money to persistent systems and return to base (manual recall) */
  private transferXpAndRecall() {
    if (this.robot.xpGained > 0) {
      this.manager.stats.addXp(this.robot.xpGained);
    }
    const coinsEarned = Math.floor(this.coinsThisRun * this.manager.buildings.getCreditMultiplier());
    this.manager.money += coinsEarned;
    // Transfer found loot to inventory (upgrades vs weapons, with quality)
    for (const loot of this.robot.lootBag) {
      if (loot.def.kind === 'weapon') {
        this.manager.upgrades.addWeaponToInventory(loot.item);
      } else {
        this.manager.upgrades.addToInventory(loot.item);
      }
    }
    this.commitRunEndStats(coinsEarned, true);
    this.manager.switchTo(new BaseState(this.manager));
  }

  /** Destroy all tile sprites — used when switching to flat LOD */

  // -------------------------------------------------------
  // Minimap — bottom-left overlay (desktop) / bottom drawer (mobile)
  // -------------------------------------------------------
  private buildMinimap() {
    const app = this.manager.app;
    const lab = this.labyrinth;
    const exploration = this.manager.exploration;
    if (!lab || !exploration) return;

    const isMobile = this.vp.mode === 'mobile';
    // Destroy persistent objects on mode flip so they rebuild cleanly at new sizes
    if (this.minimapDrawerMobileMode !== isMobile) {
      this.tearDownMinimapObjects();
      this.minimapDrawerMobileMode = isMobile;
    }

    const PADDING = MM_PADDING;
    const MARGIN = 10;
    const HEADER_H = MM_HEADER_H;
    const PANEL_BG = 0x0c0e14;
    const HANDLE_H = 28;

    let MAP_W: number, MAP_H: number, TOTAL_W: number, TOTAL_H: number;

    if (isMobile) {
      const vp = this.vp;
      const panelX = LC_MARGIN;
      const panelW = vp.w - LC_MARGIN * 2;
      const maxDrawerH = Math.max(
        HANDLE_H + 80,
        Math.floor(vp.h - tabBarH(vp) - vp.safeTop - 120),
      );
      const defaultDrawerH = Math.min(360, maxDrawerH);
      const mobileCollapsed = LabyrinthState.minimapDrawerCollapsed;
      const drawerH = mobileCollapsed
        ? HANDLE_H
        : Math.max(HANDLE_H, Math.min(maxDrawerH, LabyrinthState.minimapDrawerHeight ?? defaultDrawerH));
      const drawerY = app.screen.height - tabBarH(vp) - drawerH - LC_MARGIN;

      TOTAL_W = panelW;
      TOTAL_H = drawerH;
      MAP_W = panelW - PADDING * 2;
      MAP_H = Math.max(0, drawerH - HANDLE_H - PADDING * 2);

      this.mmMapW = MAP_W; this.mmMapH = MAP_H;
      this.minimapContainer.position.set(panelX, drawerY);

      this.drawMinimapDrawerFrame(panelW, drawerH, HANDLE_H, mobileCollapsed);

      if (mobileCollapsed || MAP_H <= 4) {
        // Hide map content when collapsed
        if (this.minimapContent) this.minimapContent.visible = false;
        if (this.minimapStatusText) this.minimapStatusText.visible = false;
        return;
      }
      if (this.minimapContent) this.minimapContent.visible = true;
    } else {
      MAP_W = MM_MAP_W;
      MAP_H = MM_MAP_H;
      TOTAL_W = MM_TOTAL_W;
      TOTAL_H = MM_TOTAL_H;
      this.mmMapW = MAP_W; this.mmMapH = MAP_H;
      this.minimapContainer.position.set(MARGIN, app.screen.height - TOTAL_H - MARGIN);
    }

    // Map area vertical origin — below header (desktop) or handle (mobile)
    const MAP_TOP_Y = (isMobile ? HANDLE_H : HEADER_H) + PADDING;

    if (!isMobile) {
      // Desktop panel background
      if (!this.minimapBg) {
        this.minimapBg = new Graphics();
        this.minimapContainer.addChild(this.minimapBg);
      }
      const bg = this.minimapBg;
      bg.clear();
      drawBevelPanel(bg, 0, 0, TOTAL_W, TOTAL_H, { fill: PANEL_BG, border: ACCENT_DIM, radius: 4, accentEdge: ACCENT_DIM, rivets: true });
      bg.roundRect(PADDING - 3, MAP_TOP_Y - 3, MAP_W + 6, MAP_H + 6, 3);
      bg.stroke({ color: 0x1a2a3a, width: 1 });

      if (!this.minimapAccent) {
        this.minimapAccent = new Graphics();
        this.minimapContainer.addChild(this.minimapAccent);
      }
      this.minimapAccent.clear();

      // Status text — reuse Text object, just update the string
      if (this._walkableTileCount < 0) {
        this._walkableTileCount = this.countWalkableTiles();
      }
      const total = this._walkableTileCount;
      const pct = total > 0 ? Math.round((exploration.visited.size / total) * 100) : 0;
      const statusStr = `Lv.${this.manager.stats.level}  |  Map: ${pct}%  |  Frontier: ${exploration.frontier.size}`;

      if (!this.minimapStatusText) {
        this.minimapStatusText = new Text({
          text: statusStr,
          style: { fontFamily: 'Offside', fontSize: 14, fill: 0xaa6677 },
        });
        this.minimapStatusText.anchor.set(0.5, 0);
        this.minimapStatusText.position.set(TOTAL_W / 2, PADDING);
        this.minimapContainer.addChild(this.minimapStatusText);
      } else {
        this.minimapStatusText.text = statusStr;
        this.minimapStatusText.visible = true;
      }
    }

    // Use incremental bounding box tracked by ExplorationData (avoids full grid scan)
    const bbox = exploration.revealedBBox;
    if (!bbox.hasRevealed) return;
    this.minimapBBoxMinX = bbox.minX; this.minimapBBoxMinY = bbox.minY;
    this.minimapBBoxMaxX = bbox.maxX; this.minimapBBoxMaxY = bbox.maxY;
    let { minimapBBoxMinX: minX, minimapBBoxMinY: minY,
          minimapBBoxMaxX: maxX, minimapBBoxMaxY: maxY } = this;

    // Add 1-tile padding around the bounding box
    minX = Math.max(0, minX - 1);
    minY = Math.max(0, minY - 1);
    maxX = Math.min(lab.width - 1, maxX + 1);
    maxY = Math.min(lab.height - 1, maxY + 1);

    const spanX = maxX - minX + 1;
    const spanY = maxY - minY + 1;
    const scale = Math.min(MAP_W / spanX, MAP_H / spanY);

    const renderedW = spanX * scale;
    const renderedH = spanY * scale;
    const offsetX = PADDING + (MAP_W - renderedW) / 2;
    const offsetY = MAP_TOP_Y + (MAP_H - renderedH) / 2;

    // ── Canvas2D minimap — renders tiles as pixels, displayed as a single Sprite ──
    // This replaces the old Graphics-based approach that created tens of thousands
    // of rect() commands, causing PixiJS geometry tessellation to freeze the browser.

    // Use pixel-per-tile when scale < 1, otherwise ceil to avoid sub-pixel gaps
    const pxPerTile = Math.max(1, Math.ceil(scale));
    const canvasW = spanX * pxPerTile;
    const canvasH = spanY * pxPerTile;

    // Create or resize the offscreen canvas
    if (!this.minimapCanvas) {
      this.minimapCanvas = document.createElement('canvas');
      this.minimapCtx = this.minimapCanvas.getContext('2d')!;
    }
    if (this.minimapCanvas.width !== canvasW || this.minimapCanvas.height !== canvasH) {
      this.minimapCanvas.width = canvasW;
      this.minimapCanvas.height = canvasH;
      // Force texture re-creation after resize so PixiJS picks up the new dimensions
      if (this.minimapSprite) {
        this.minimapTexture!.destroy(true);
        this.minimapTexture = Texture.from({ resource: this.minimapCanvas, scaleMode: 'nearest' });
        this.minimapSprite.texture = this.minimapTexture;
      }
    }

    const ctx = this.minimapCtx!;

    // Color constants as CSS strings (brightened ~68% vs. original so revealed
    // tiles read clearly against the near-black unexplored background)
    const CSS_WALL     = '#611a29';
    const CSS_BASE     = '#2b6150';
    const CSS_VISITED  = '#47506c';
    const CSS_FRONTIER = '#617d50';
    const CSS_FLOOR    = '#364350';

    // Clear to transparent so the panel background shows through for fog areas
    ctx.clearRect(0, 0, canvasW, canvasH);

    // Draw each tile as a small rect on the canvas
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        if (!exploration.revealed[y]![x]) continue; // leave as fog background

        const tile = lab.tiles[y]![x];
        if (tile === Tile.Wall) {
          ctx.fillStyle = CSS_WALL;
        } else if (tile === Tile.Base || tile === Tile.Door) {
          ctx.fillStyle = CSS_BASE;
        } else if (exploration.visited.has(`${x},${y}`)) {
          ctx.fillStyle = CSS_VISITED;
        } else if (exploration.frontier.has(`${x},${y}`)) {
          ctx.fillStyle = CSS_FRONTIER;
        } else {
          ctx.fillStyle = CSS_FLOOR;
        }

        ctx.fillRect(
          (x - minX) * pxPerTile,
          (y - minY) * pxPerTile,
          pxPerTile,
          pxPerTile,
        );
      }
    }

    // Building footprints on minimap
    const bs = this.manager.buildings;
    for (const b of bs.buildings) {
      const def = getBuildingById(b.id);
      if (!def) continue;
      ctx.fillStyle = buildingColorToCSS(def.color);
      const bx = (bs.baseOriginX + b.gridX - minX) * pxPerTile;
      const by = (bs.baseOriginY + b.gridY - minY) * pxPerTile;
      ctx.fillRect(bx, by, def.footprintW * pxPerTile, def.footprintH * pxPerTile);
    }

    // Base marker
    const bmx = Math.round((lab.baseTile.x - minX) * pxPerTile);
    const bmy = Math.round((lab.baseTile.y - minY) * pxPerTile);
    const mSize = Math.max(3, pxPerTile * 2);
    ctx.fillStyle = '#dd2233';
    ctx.fillRect(bmx - mSize / 2, bmy - mSize / 2, mSize, mSize);

    // Robot + enemy blips are drawn by the per-frame radar overlay (drawRadarOverlay)

    // Upload canvas to PixiJS as a single Sprite
    // Create content container + mask (once). Mask geometry is redrawn whenever
    // dimensions change (mobile drawer resize).
    if (!this.minimapContent) {
      this.minimapContent = new Container();
      this.minimapMask = new Graphics();
      this.minimapContainer.addChild(this.minimapMask);
      this.minimapContent.mask = this.minimapMask;
      this.minimapContainer.addChild(this.minimapContent);
    }
    this.minimapMask!.clear();
    this.minimapMask!.rect(PADDING, MAP_TOP_Y, MAP_W, MAP_H);
    this.minimapMask!.fill(0xffffff);

    if (this.minimapSprite) {
      // Update existing texture from canvas
      this.minimapTexture!.source.resource = this.minimapCanvas;
      this.minimapTexture!.source.update();
    } else {
      this.minimapTexture = Texture.from({ resource: this.minimapCanvas, scaleMode: 'nearest' });
      this.minimapSprite = new Sprite(this.minimapTexture);
      this.minimapContent.addChild(this.minimapSprite);
    }

    // Position and scale the sprite to match the expected minimap area
    this.minimapSprite.x = offsetX;
    this.minimapSprite.y = offsetY;
    this.minimapSprite.width = renderedW;
    this.minimapSprite.height = renderedH;

    // Cache coordinate mapping for the per-frame radar overlay
    this.mmMinX = minX;
    this.mmMinY = minY;
    this.mmScale = scale;
    this.mmOffsetX = offsetX;
    this.mmOffsetY = offsetY;

    // Pick an initial zoom so the radar circle fills ~30% of the map width.
    // circleDiameter(z) = 2 * detR * mmScale * z → solve for z at 30% MAP_W.
    // Only runs once — later rebuilds preserve the player's scroll-zoom.
    if (!this.minimapZoomInitialised && this.robot) {
      const detR = this.robot.detectionRadius;
      if (detR > 0 && this.mmScale > 0) {
        const targetDiameter = MM_MAP_W * 0.30;
        const z = targetDiameter / (2 * detR * this.mmScale);
        this.minimapZoom = Math.max(MM_MIN_ZOOM, Math.min(MM_MAX_ZOOM, z));
        this.minimapZoomInitialised = true;
      }
    }

    // Create radar overlay Graphics (sits on top of the tile sprite, inside content container)
    if (!this.radarOverlay) {
      this.radarOverlay = new Graphics();
      this.minimapContent.addChild(this.radarOverlay);
    }
  }

  /** Draw the mobile drawer panel + grip handle (mobile only). */
  private drawMinimapDrawerFrame(w: number, h: number, handleH: number, collapsed: boolean) {
    // Panel bg — rebuild each call since size may change with drag
    if (!this.minimapDrawerBg) {
      this.minimapDrawerBg = new Graphics();
      this.minimapContainer.addChildAt(this.minimapDrawerBg, 0);
    }
    const bg = this.minimapDrawerBg;
    bg.clear();
    drawBevelPanel(bg, 0, 0, w, h, { fill: 0x0c0e14, border: ACCENT_DIM, radius: 4, accentEdge: ACCENT_DIM, rivets: true });

    // Handle bar on top
    if (!this.minimapDrawerHandle) {
      this.minimapDrawerHandle = new Container();
      this.minimapContainer.addChild(this.minimapDrawerHandle);
      this.minimapDrawerHandle.eventMode = 'static';
      this.minimapDrawerHandle.cursor = 'ns-resize';
      this.minimapDrawerHandle.on('pointerdown', (e: FederatedPointerEvent) => this.onMinimapDrawerDown(e));
    }
    const handle = this.minimapDrawerHandle;
    handle.removeChildren();
    handle.position.set(0, 0);
    const hbg = new Graphics();
    hbg.rect(0, 0, w, handleH);
    hbg.fill({ color: ACCENT_DARK, alpha: 0.6 });
    hbg.moveTo(0, handleH); hbg.lineTo(w, handleH);
    hbg.stroke({ color: ACCENT_DIM, width: 1 });
    const gripY = handleH / 2;
    for (let i = -1; i <= 1; i++) {
      const gx = w / 2 + i * 14;
      hbg.moveTo(gx - 5, gripY); hbg.lineTo(gx + 5, gripY);
    }
    hbg.stroke({ color: TEXT_PRIMARY, width: 2, alpha: 0.7 });
    handle.addChild(hbg);
    // Hit area covers full handle bar
    handle.hitArea = { contains: (x: number, y: number) => x >= 0 && x <= w && y >= 0 && y <= handleH };

    if (collapsed) {
      const label = new Text({
        text: 'MAP  ▲',
        style: { ...FONT_DETAIL, fill: TEXT_PRIMARY, dropShadow: GUGI_SHADOW },
      });
      label.anchor.set(0.5, 0.5);
      label.position.set(w / 2, handleH / 2 - 10);
      handle.addChild(label);
    }
  }

  private onMinimapDrawerDown(e: FederatedPointerEvent) {
    const vp = this.vp;
    const HANDLE_H = 28;
    const panelW = vp.w - LC_MARGIN * 2;
    const maxDrawerH = Math.max(
      HANDLE_H + 80,
      Math.floor(vp.h - tabBarH(vp) - vp.safeTop - 120),
    );
    const defaultDrawerH = Math.min(360, maxDrawerH);
    const currentH = LabyrinthState.minimapDrawerCollapsed
      ? HANDLE_H
      : (LabyrinthState.minimapDrawerHeight ?? defaultDrawerH);
    this.minimapDrawerDrag = {
      startY: e.globalY,
      startH: currentH,
      minH: HANDLE_H,
      maxH: maxDrawerH,
      dragged: false,
      previewLine: null,
      panelX: LC_MARGIN,
      panelW,
    };
    e.stopPropagation();
  }

  private onMinimapDrawerMove(e: FederatedPointerEvent) {
    const dd = this.minimapDrawerDrag;
    if (!dd) return;
    const dy = e.globalY - dd.startY;
    if (Math.abs(dy) > 3) dd.dragged = true;
    const newH = Math.max(dd.minH, Math.min(dd.maxH, dd.startH - dy));
    if (!dd.previewLine) {
      dd.previewLine = new Graphics();
      this.uiContainer.addChild(dd.previewLine);
    }
    const previewY = this.manager.app.screen.height - tabBarH(this.vp) - LC_MARGIN - newH;
    dd.previewLine.clear();
    dd.previewLine.moveTo(dd.panelX, previewY);
    dd.previewLine.lineTo(dd.panelX + dd.panelW, previewY);
    dd.previewLine.stroke({ color: ACCENT, width: 2, alpha: 0.9 });
  }

  private onMinimapDrawerUp(e: FederatedPointerEvent) {
    const dd = this.minimapDrawerDrag;
    if (!dd) return;
    this.minimapDrawerDrag = null;
    if (dd.previewLine) {
      this.uiContainer.removeChild(dd.previewLine);
      dd.previewLine.destroy();
    }
    if (dd.dragged) {
      const dy = e.globalY - dd.startY;
      const newH = Math.max(dd.minH, Math.min(dd.maxH, dd.startH - dy));
      LabyrinthState.minimapDrawerHeight = newH;
      LabyrinthState.minimapDrawerCollapsed = newH <= dd.minH + 2;
    } else {
      // Tap without drag → toggle
      LabyrinthState.minimapDrawerCollapsed = !LabyrinthState.minimapDrawerCollapsed;
    }
    LabyrinthState.persistMinimapDrawer();
    this.buildMinimap();
  }

  private toggleMinimapDrawer() {
    LabyrinthState.minimapDrawerCollapsed = !LabyrinthState.minimapDrawerCollapsed;
    LabyrinthState.persistMinimapDrawer();
    this.buildMinimap();
  }

  /** Destroy persistent minimap Graphics/Text/Sprite — forces a clean rebuild at a new size/mode. */
  private tearDownMinimapObjects() {
    if (this.minimapBg) { this.minimapBg.destroy(); this.minimapBg = null; }
    if (this.minimapAccent) { this.minimapAccent.destroy(); this.minimapAccent = null; }
    if (this.minimapStatusText) { this.minimapStatusText.destroy(); this.minimapStatusText = null; }
    if (this.minimapDrawerBg) { this.minimapDrawerBg.destroy(); this.minimapDrawerBg = null; }
    if (this.minimapDrawerHandle) { this.minimapDrawerHandle.destroy({ children: true }); this.minimapDrawerHandle = null; }
    if (this.radarOverlay) { this.radarOverlay.destroy(); this.radarOverlay = null; }
    if (this.minimapMask) { this.minimapMask.destroy(); this.minimapMask = null; }
    if (this.minimapContent) { this.minimapContent.destroy({ children: true }); this.minimapContent = null; }
    if (this.minimapSprite) { this.minimapSprite.destroy(); this.minimapSprite = null; }
    if (this.minimapTexture) { this.minimapTexture.destroy(true); this.minimapTexture = null; }
    this.minimapCanvas = null;
    this.minimapCtx = null;
  }

  /**
   * Per-frame radar sweep overlay: rotating green line + trail + enemy blips.
   * Runs every frame for smooth animation, unlike buildMinimap (0.5 s).
   */
  private drawRadarOverlay(dt: number) {
    const g = this.radarOverlay;
    if (!g || !this.minimapContent) return;
    // Skip entirely when mobile drawer is collapsed
    if (this.vp.mode === 'mobile' && LabyrinthState.minimapDrawerCollapsed) {
      g.clear();
      return;
    }
    g.clear();

    // Apply minimap zoom: scale content and center on robot
    const z = this.minimapZoom;
    if (z > 1) {
      const robotCX = this.mmOffsetX + (this.robot.displayX - this.mmMinX) * this.mmScale;
      const robotCY = this.mmOffsetY + (this.robot.displayY - this.mmMinY) * this.mmScale;
      const mapCenterX = MM_PADDING + this.mmMapW / 2;
      const mapCenterY = MM_PADDING + MM_HEADER_H + this.mmMapH / 2;
      this.minimapContent.scale.set(z);
      this.minimapContent.x = mapCenterX - robotCX * z;
      this.minimapContent.y = mapCenterY - robotCY * z;
    } else {
      this.minimapContent.scale.set(1);
      this.minimapContent.x = 0;
      this.minimapContent.y = 0;
    }

    // Advance sweep angle
    const prevAngle = this.radarAngle;
    this.radarAngle = (this.radarAngle + dt * RADAR_SPEED) % (Math.PI * 2);
    const angleDelta = dt * RADAR_SPEED;

    const rx = this.robot.displayX;
    const ry = this.robot.displayY;
    const detR = this.robot.detectionRadius;
    const detRSq = detR * detR;

    // Robot center and radius in minimap-container coordinates
    const cx = this.mmOffsetX + (rx - this.mmMinX) * this.mmScale;
    const cy = this.mmOffsetY + (ry - this.mmMinY) * this.mmScale;
    const r = detR * this.mmScale;

    // ── Ping enemies swept by the line this frame ──
    for (const e of this.entities) {
      if (!e.alive || e.type !== 'enemy') continue;
      const dx = e.x - rx;
      const dy = e.y - ry;
      if (dx * dx + dy * dy > detRSq) continue;
      const bearing = ((Math.atan2(dy, dx) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const normPrev = ((prevAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const delta = ((bearing - normPrev) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      if (delta <= angleDelta) e.radarTimer = RADAR_BLIP_DURATION;
    }
    const sm = this.swarmManager;
    for (let i = 0; i < sm.alive.length; i++) {
      if (!sm.alive[i]) continue;
      const dx = sm.x[i]! - rx;
      const dy = sm.y[i]! - ry;
      if (dx * dx + dy * dy > detRSq) continue;
      const bearing = ((Math.atan2(dy, dx) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const normPrev = ((prevAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const delta = ((bearing - normPrev) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      if (delta <= angleDelta) sm.radarTimer[i] = RADAR_BLIP_DURATION;
    }

    // ── Tick radar timers ──
    for (const e of this.entities) {
      if (e.radarTimer > 0) e.radarTimer = Math.max(0, e.radarTimer - dt);
    }
    for (let i = 0; i < sm.alive.length; i++) {
      if (sm.radarTimer[i]! > 0) sm.radarTimer[i] = Math.max(0, sm.radarTimer[i]! - dt);
    }

    // ── Waypoint trail — simplified dotted line through corridors ──
    // Red = autonomous exploration path, blue = path to manual marker.
    const pathPts = this.robot.getPathPoints(1.0);
    if (pathPts.length >= 2) {
      const isMarkerRoute = this.robot.state === 'moving_to_marker' && !!this.robot.marker;
      const trailColor = isMarkerRoute ? 0x44aaff : 0xcc3344;
      const pulse = 0.55 + Math.sin(this._frameTime * 3) * 0.15;
      const dotSpacingPx = 5;
      const dotRadius = Math.max(0.8, this.mmScale * 0.22);
      let residual = 0;
      for (let i = 0; i < pathPts.length - 1; i++) {
        const p0 = pathPts[i]!;
        const p1 = pathPts[i + 1]!;
        const x0 = this.mmOffsetX + (p0[0] - this.mmMinX) * this.mmScale;
        const y0 = this.mmOffsetY + (p0[1] - this.mmMinY) * this.mmScale;
        const x1 = this.mmOffsetX + (p1[0] - this.mmMinX) * this.mmScale;
        const y1 = this.mmOffsetY + (p1[1] - this.mmMinY) * this.mmScale;
        const segDx = x1 - x0;
        const segDy = y1 - y0;
        const segLen = Math.hypot(segDx, segDy);
        if (segLen < 0.001) continue;
        const nx = segDx / segLen;
        const ny = segDy / segLen;
        let d = residual;
        while (d <= segLen) {
          g.circle(x0 + nx * d, y0 + ny * d, dotRadius);
          g.fill({ color: trailColor, alpha: pulse });
          d += dotSpacingPx;
        }
        residual = d - segLen;
      }
      // Destination dot at path end (only for auto — manual marker drawn below)
      if (!isMarkerRoute) {
        const end = pathPts[pathPts.length - 1]!;
        const ex = this.mmOffsetX + (end[0] - this.mmMinX) * this.mmScale;
        const ey = this.mmOffsetY + (end[1] - this.mmMinY) * this.mmScale;
        g.circle(ex, ey, dotRadius * 2);
        g.fill({ color: trailColor, alpha: Math.min(1, pulse + 0.25) });
      }
    }

    // Manual marker indicator — pulsing ring wherever the player clicked
    if (this.robot.marker) {
      const [markX, markY] = this.robot.marker;
      const mpx = this.mmOffsetX + (markX - this.mmMinX) * this.mmScale;
      const mpy = this.mmOffsetY + (markY - this.mmMinY) * this.mmScale;
      const mPulse = 0.7 + Math.sin(this._frameTime * 3) * 0.2;
      const mR = Math.max(2.5, this.mmScale * 0.55);
      g.circle(mpx, mpy, mR * (0.9 + Math.sin(this._frameTime * 3) * 0.15));
      g.stroke({ color: 0x44aaff, width: 1.2, alpha: mPulse });
      g.circle(mpx, mpy, Math.max(1, mR * 0.3));
      g.fill({ color: 0x44aaff, alpha: 0.95 });
    }

    // ── Draw green gradient sweep trail ──
    for (let i = 0; i < RADAR_TRAIL_SEGMENTS; i++) {
      const frac0 = i / RADAR_TRAIL_SEGMENTS;
      const frac1 = (i + 1) / RADAR_TRAIL_SEGMENTS;
      const a0 = this.radarAngle - frac0 * RADAR_TRAIL_ANGLE;
      const a1 = this.radarAngle - frac1 * RADAR_TRAIL_ANGLE;
      const alpha = (1 - frac0) * 0.18;
      g.moveTo(cx, cy);
      g.arc(cx, cy, r, a1, a0);
      g.closePath();
      g.fill({ color: 0x00ff44, alpha });
    }

    // ── Sweep line ──
    const lx = cx + Math.cos(this.radarAngle) * r;
    const ly = cy + Math.sin(this.radarAngle) * r;
    g.moveTo(cx, cy);
    g.lineTo(lx, ly);
    g.stroke({ color: 0x00ff88, width: 1.5, alpha: 0.85 });

    // ── Detection radius ring ──
    g.circle(cx, cy, r);
    g.stroke({ color: 0x00ff44, width: 0.6, alpha: 0.15 });

    // ── Robot dot ──
    g.circle(cx, cy, Math.max(2, this.mmScale * 0.8));
    g.fill({ color: 0x44aaff, alpha: 0.9 });

    // ── Enemy blips (accelerating fade: slow initially, fast near end) ──
    const dotR = Math.max(1.5, this.mmScale * 0.5);
    for (const e of this.entities) {
      if (e.type !== 'enemy' || !e.alive || e.radarTimer <= 0) continue;
      const elapsed = RADAR_BLIP_DURATION - e.radarTimer;
      const t = elapsed / RADAR_BLIP_DURATION; // 0→1
      const alpha = 1 - t * t; // accelerating fade
      const ex = this.mmOffsetX + (e.x - this.mmMinX) * this.mmScale;
      const ey = this.mmOffsetY + (e.y - this.mmMinY) * this.mmScale;
      g.circle(ex, ey, dotR);
      g.fill({ color: 0x44ff66, alpha });
    }
    for (let i = 0; i < sm.alive.length; i++) {
      if (!sm.alive[i] || sm.radarTimer[i]! <= 0) continue;
      const elapsed = RADAR_BLIP_DURATION - sm.radarTimer[i]!;
      const t = elapsed / RADAR_BLIP_DURATION;
      const alpha = 1 - t * t;
      const sx = this.mmOffsetX + (sm.x[i]! - this.mmMinX) * this.mmScale;
      const sy = this.mmOffsetY + (sm.y[i]! - this.mmMinY) * this.mmScale;
      g.circle(sx, sy, dotR);
      g.fill({ color: 0x44ff66, alpha });
    }
  }

  private countWalkableTiles(): number {
    let count = 0;
    for (let y = 0; y < this.labyrinth.height; y++)
      for (let x = 0; x < this.labyrinth.width; x++)
        if (this.labyrinth.isWalkable(x, y)) count++;
    return count;
  }
}
